import {
  Component, computed, DestroyRef, effect, ElementRef, inject, NgZone, OnInit, signal, untracked, viewChild
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PinnedTableController} from '../../../_ui/tables/pinned-table';
import {of, Subject} from 'rxjs';
import {catchError, filter, skip, switchMap} from 'rxjs/operators';
import {DatePipe, DecimalPipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {WorktimeStatsHttpService} from './worktime-stats-http.service';
import {roundHours, WorktimeActivity, WorktimeStatsDto, WorktimeStatsOverviewDto} from './worktime-stats.models';
import {EmployeeHttpService} from '../x-http-requests/employee-http.service';
import {EmploymentRelationshipHttpService} from '../x-http-requests/employment-relationship-http.service';
import {SelectComponent} from '../../../_ui/components/controls/select/select.component';
import {AppRoutes} from '../../../enums/routes';
import {EmployeeDto} from '../x-models/employee-dto';
import {EmploymentStatus, employmentStartOf, employmentStatusOf} from '../x-models/employment-status';
import {EmployeeService} from '../_services/_dto-services/employee.service';
import {PermissionService} from '../../../_auth/permission.service';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlignmentService} from '../../../_services/alignment.service';
import {AlertLevel} from '../../../enums/alert-level';
import {FormContainerComponent} from '../../../_components/form-container/form-container.component';
import {
  EmploymentRelationshipPopupComponent
} from '../x-popups/employment-relationship-popup/employment-relationship-popup.component';
import {
  EmployeeAttributePopupComponent
} from '../../x-components/employee-attribute-popup/employee-attribute-popup.component';
import {DownloadComponent} from '../../x-components/download/download.component';
import {DownloadInformation} from '../../x-components/x-models/download-information';
import {ServerEndpoints} from '../../../server.endpoints';
import {GrantHttpService} from '../access/grant-http.service';
import {
  InviteDelivery, InviteEmailDelivery, RoleGrantInvitation, looksLikeEmail
} from '../access/grant.models';
import {InviteCodeDialogComponent} from '../access/invite-code-dialog.component';
import {InviteDeliveryChoiceComponent} from '../access/invite-delivery-choice.component';
import {ContactChannelChipComponent} from '../../../_ui/components/contact-chip/contact-chip.component';
import {ContactQuickLinkHttpService} from '../contact-quick-links/contact-quick-link-http.service';
import {ContactChannelQuickLink} from '../contact-quick-links/contact-quick-link.models';
import {contactChannelLabel} from './contact-requirements/contact-channels';

/** A selectable option for the relationship filter dropdown. */
interface FilterOption {
  id: string;
  name: string;
}

/** One merged roster row: the employee entity + their worktime metrics for the selected range. */
export interface EmployeeRow {
  employee: EmployeeDto;
  stats?: WorktimeStatsDto;
}

/**
 * How long a row's enter/leave animation is allowed to matter. The CSS runs for 200ms (the style guide's
 * `duration-200` house default); the extra slack covers the frame Angular needs to pick the animation up and
 * the one it needs to drop the node afterwards. After this, the employee id is un-marked so a LATER filter or
 * sort change involving the same row is instant again.
 */
const ROW_ANIMATION_RESET_MS = 500;

/** The classes `animate.enter` / `animate.leave` apply — see the component styles. */
const ROW_ENTER_CLASS = 'cq-anim-in';
const ROW_LEAVE_CLASS = 'cq-anim-out';


/** The pending end-employment / rehire confirmation dialog. */
interface LifecycleDialog {
  mode: 'end' | 'rehire';
  employee: EmployeeDto;
  /** yyyy-MM-dd date the action applies at (defaults to today). */
  dateStr: string;
}

/**
 * THE company employees page ("Mitarbeiter") — the former separate employee list and worktime-stats overview
 * merged into one screen (employee lifecycle rework, #140). It lists every employee with their entity data
 * (attributes, contact channels, e-mail, login toggle, employment relationship) and their worktime metrics
 * (hours, shifts, averages) for the selected range, with aggregate cards on top.
 *
 * Contact (#558) is ONE column: „Kontakt" holds a chip per consented channel and falls back to the legacy,
 * unconfirmed Telegram handle from the employee profile. It sits in the slot the old „Telegram / Email"
 * column held, which is now „E-Mail" alone — so the feature costs the table no column and no width.
 *
 * Employment is modeled as periods now: the default view shows only the currently employed; "Ehemalig" reveals
 * no-longer-employed employees, marked "Nicht mehr beschäftigt". There is NO delete — the row action ends the
 * employment (closing the open period, keeping all historical data), and former employees offer a rehire action
 * that opens a new period. Follows the style-guide "data table + card fallback" and "list toolbar" patterns
 * (table at md+, stacked cards below).
 */
@Component({
  selector: 'app-employees',
  standalone: true,
  // The host must be a block: as the default inline custom element (width 0) it doesn't contain the max-w-7xl
  // child, so the wide table's overflow leaks up to the app-shell scroll container and the WHOLE PAGE scrolls
  // horizontally at large widths — the table must scroll inside its own overflow-x-auto wrapper only (#140).
  host: {class: 'block min-w-0'},
  imports: [FormsModule, RouterLink, DatePipe, DecimalPipe, SelectComponent, FormContainerComponent,
    EmploymentRelationshipPopupComponent, EmployeeAttributePopupComponent, DownloadComponent,
    InviteCodeDialogComponent, InviteDeliveryChoiceComponent, ContactChannelChipComponent],
  template: `
    <div class="mx-auto max-w-7xl space-y-6 p-4">
      <div>
        <h1 class="text-2xl font-bold tracking-tight text-gray-900">Mitarbeiter</h1>
        <p class="mt-1 text-sm text-gray-400">
          Alle Mitarbeiter:innen mit Arbeitszeiten und Statistiken. Beschäftigungen werden beendet statt gelöscht —
          ehemalige Mitarbeiter:innen bleiben mit ihrer Historie erhalten.
        </p>
      </div>

      <!-- Add employee (admins). The employee filter now lives in the toolbar as a multi-select, so the
           form-container's built-in search is disabled (hasSearch=false). -->
      @if (PermissionService.CheckPermission(PermissionService.Company_Employee_Add())) {
        <layout-form-container (clear)="resetAddForm()" [hasSearch]="false"
                               (submit)="add()" [isCollapsed]="true" [preventClearOnCollapseChange]="true"
                               clearText="Felder leeren"
                               submitText="Mitarbeiter anlegen">
          <div class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="block text-sm font-medium leading-6 text-gray-900" for="first-name">Vorname</label>
              <input [ngModel]="addFirst()" (ngModelChange)="addFirst.set($event)" autocomplete="given-name"
                     class="mt-2 block h-10 w-full rounded-xl border border-gray-200 px-3 text-sm text-gray-900 focus:border-theme-primary-500 focus:outline-none focus:ring-1 focus:ring-theme-primary-500"
                     id="first-name" name="first-name" type="text">
            </div>
            <div class="sm:col-span-3">
              <label class="block text-sm font-medium leading-6 text-gray-900" for="last-name">Nachname</label>
              <input [ngModel]="addLast()" (ngModelChange)="addLast.set($event)" autocomplete="family-name"
                     class="mt-2 block h-10 w-full rounded-xl border border-gray-200 px-3 text-sm text-gray-900 focus:border-theme-primary-500 focus:outline-none focus:ring-1 focus:ring-theme-primary-500"
                     id="last-name" name="last-name" type="text">
            </div>
            <div class="sm:col-span-3">
              <label class="block text-sm font-medium leading-6 text-gray-900" for="email">Email Adresse</label>
              <input [ngModel]="addEmail()" (ngModelChange)="addEmail.set($event)" autocomplete="email"
                     class="mt-2 block h-10 w-full rounded-xl border border-gray-200 px-3 text-sm text-gray-900 focus:border-theme-primary-500 focus:outline-none focus:ring-1 focus:ring-theme-primary-500"
                     id="email" name="email" type="email">
            </div>
            <div class="sm:col-span-3">
              <label class="block text-sm font-medium leading-6 text-gray-900" for="add-relationship">Arbeitsverhältnis</label>
              <!-- Optional: directly assign an employment relationship on creation (applied right after the add). -->
              <cqme-select id="add-relationship" class="mt-2 block" [options]="relationships()" [labelKey]="'name'"
                           [valueKey]="'id'" size="md" [clearable]="true" placeholder="Kein Arbeitsverhältnis"
                           ariaLabel="Arbeitsverhältnis für neuen Mitarbeiter"
                           [current]="addRelationshipId()" (currentChange)="setAddRelationship($event)"></cqme-select>
            </div>
            <div class="sm:col-span-3">
              <label class="block text-sm font-medium leading-6 text-gray-900" for="employment-start">Eintritt</label>
              <input [ngModel]="addStart()" (ngModelChange)="addStart.set($event)"
                     class="mt-2 block h-10 w-full rounded-xl border border-gray-200 px-3 text-sm text-gray-900 focus:border-theme-primary-500 focus:outline-none focus:ring-1 focus:ring-theme-primary-500"
                     id="employment-start" name="employment-start" type="date">
            </div>
            <div class="sm:col-span-3">
              <!-- How the new employee gets a way in (#279). The invitation is issued right after the employee
                   exists, because it has to name them; "Keine Einladung" stays a real choice, so creating a
                   record for somebody who will never sign in costs nothing.

                   The SHARED segmented choice since #568 — the same control and the same words as access
                   management and an employee's Anmeldung card. It used to be a cqme-select that REMOVED the
                   e-mail option while the address field was empty; the option is now rendered and disabled,
                   with its reason where the option is rather than beside a list it has fallen out of. -->
              <company-invite-delivery-choice class="block" name="add-invite" legend="Einladung erstellen"
                [allowNone]="true" [value]="effectiveInvite()" [emailAvailable]="canMailInvite()"
                unavailableReason="Per E-Mail senden ist erst möglich, wenn eine E-Mail-Adresse eingetragen ist."
                (valueChange)="setAddInvite($event)"/>

              <!-- ONE binding toggle (#584), where there used to be a checkbox for the binding and a switch for
                   its strength. Their only distinguishable state — bound but not enforced — refuses nobody,
                   because the gate falls back to the code alone for any address the provider does not vouch
                   for. So this switch decides BindToEmployeeEmail and EnforceBoundEmail together.

                   Offered only where it can be honoured: the server derives the binding from the employee's
                   stored address, so asking for it without a usable one answers E_GRANT_INVITE_EMAIL_REQUIRED.
                   "Keine Einladung" hides it too — there is no invitation for it to constrain. Rarely the right
                   choice here (an employee-link code is handed to somebody standing in the room), hence OFF by
                   default and a hint that names the cost. -->
              @if (canBindAddEmail()) {
                <div class="mt-3 flex items-start justify-between gap-3 rounded-xl border border-gray-200
                            bg-white p-3">
                  <span class="min-w-0">
                    <span id="add-binding-label" class="block text-sm font-medium text-gray-900">
                      Nur mit {{ addEmail().trim() }} einlösbar
                    </span>
                    <span id="add-binding-hint" class="block text-xs text-gray-700">
                      Eingeschaltet: nur ein Konto, dessen vom Anbieter bestätigte Adresse genau dieser
                      entspricht, kann die Einladung annehmen. Apple „E-Mail-Adresse verbergen“ wird abgelehnt.
                      Ausgeschaltet: jedes Konto kann sie annehmen.
                    </span>
                  </span>
                  <label class="relative inline-flex shrink-0 cursor-pointer items-center">
                    <input id="add-binding" type="checkbox" class="peer sr-only"
                           [checked]="addBindEmail()"
                           aria-labelledby="add-binding-label" aria-describedby="add-binding-hint"
                           (change)="addBindEmail.set(isChecked($event))">
                    <span class="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-0.5 after:top-0.5
                                 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all
                                 peer-checked:bg-theme-primary-600 peer-checked:after:translate-x-5"></span>
                  </label>
                </div>
              }
            </div>
          </div>
        </layout-form-container>
      }

      <!-- The one moment the invitation code exists. Shown after the employee was created, and dismissible only
           by its own button — see InviteCodeDialogComponent. -->
      @if (issuedInvitation(); as invitation) {
        <company-invite-code-dialog [invitation]="invitation"
                                    (closed)="issuedInvitation.set(null)"></company-invite-code-dialog>
      }

      <!-- Filter toolbar -->
      <div class="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm lg:flex-row lg:flex-wrap lg:items-end">
        <!-- Employment status (segmented): default = currently employed; "Ehemalig" reveals former employees. -->
        <div class="flex flex-col">
          <span class="mb-1 text-xs font-medium text-gray-500">Beschäftigung</span>
          <div class="inline-flex h-10 w-full items-center gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm lg:w-auto">
            @for (opt of activities; track opt.value) {
              <button type="button" (click)="setActivity(opt.value)"
                      class="flex h-full flex-1 items-center justify-center whitespace-nowrap rounded-lg px-3 text-center text-sm font-medium leading-5 transition-all duration-200 lg:flex-none"
                      [class.bg-theme-primary-50]="activity() === opt.value"
                      [class.text-theme-primary-600]="activity() === opt.value"
                      [class.shadow-sm]="activity() === opt.value"
                      [class.text-gray-500]="activity() !== opt.value"
                      [class.hover:bg-gray-100]="activity() !== opt.value">{{ opt.label }}</button>
            }
          </div>
        </div>

        <!-- Employee (multi-select) — replaces the free-text search: pick specific people to narrow the list. -->
        <div class="flex min-w-0 flex-1 flex-col">
          <span class="mb-1 text-xs font-medium text-gray-500">Mitarbeiter</span>
          <cqme-select [options]="employeeOptions()" [labelKey]="'name'" [valueKey]="'id'" [multiple]="true"
                       size="md" [clearable]="true" placeholder="Alle" ariaLabel="Mitarbeiter"
                       [current]="employeeIds()" (currentChange)="setEmployees($event)"></cqme-select>
        </div>

        <!-- Employment relationship (multi-select) -->
        <div class="flex min-w-0 flex-1 flex-col">
          <span class="mb-1 text-xs font-medium text-gray-500">Arbeitsverhältnis</span>
          <cqme-select [options]="relationships()" [labelKey]="'name'" [valueKey]="'id'" [multiple]="true"
                       size="md" [clearable]="true" placeholder="Alle" ariaLabel="Arbeitsverhältnis"
                       [current]="relationshipIds()" (currentChange)="setRelationships($event)"></cqme-select>
        </div>

        <!-- Stats date range -->
        <div class="flex flex-col">
          <span class="mb-1 text-xs font-medium text-gray-500">Von</span>
          <!-- The visible "Von"/"Bis" captions are spans, not <label for>s, so screen readers had nothing to
               announce for these two inputs (axe "label", critical). aria-label restores the accessible name
               without any visual change. NOTE: never use a backtick in this template — it is a TS template
               literal, and a stray backtick silently terminates it mid-markup. -->
          <input type="date" [ngModel]="startStr()" (ngModelChange)="setStart($event)"
                 aria-label="Auswertungszeitraum von"
                 class="h-10 rounded-xl border border-gray-200 px-3 text-sm focus:border-theme-primary-500 focus:outline-none focus:ring-1 focus:ring-theme-primary-500"/>
        </div>
        <div class="flex flex-col">
          <span class="mb-1 text-xs font-medium text-gray-500">Bis</span>
          <input type="date" [ngModel]="endStr()" (ngModelChange)="setEnd($event)"
                 aria-label="Auswertungszeitraum bis"
                 class="h-10 rounded-xl border border-gray-200 px-3 text-sm focus:border-theme-primary-500 focus:outline-none focus:ring-1 focus:ring-theme-primary-500"/>
        </div>
      </div>

      <!-- Toolbar row: selection mode / batch actions / download -->
      <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        @if (!PermissionService.IsPayroll()) {
          <button type="button" (click)="toggleSelectionMode()" data-testid="selection-toggle"
                  class="font-semibold text-theme-primary-700 hover:text-theme-primary-900">
            {{ selectionMode() ? 'Fertig' : 'Bearbeiten' }}
          </button>
        }
        @if (selectionMode() && PermissionService.CheckPermission(PermissionService.CombinedPermission_EmployeeEditEmployeeRelationship())) {
          <button type="button" (click)="relationshipPopupVisible = true" [disabled]="selectedIds().length === 0"
                  data-testid="batch-relationship"
                  class="font-medium text-theme-primary-700 hover:text-theme-primary-900 disabled:pointer-events-none disabled:text-gray-400">
            Arbeitsverhältnis festlegen
          </button>
        }
        @if (PermissionService.CheckPermission(PermissionService.Document_Employee_Download()) && !PermissionService.IsPayroll()) {
          <div class="ml-auto flex items-center">
            <button type="button" (click)="downloadExtended = !downloadExtended"
                    [class.!border-red-600]="downloadExtended" [class.!text-red-600]="downloadExtended"
                    class="mr-3 rounded-xl border-2 border-gray-300 px-2 py-0.5 text-gray-300">Erweitert</button>
            <app-download [DownloadInformation]="downloadInformation" [Params]="'extended=' + downloadExtended"
                          [Route]="ServerEndpoints.Document_Employee_Download" class="place-self-center"/>
          </div>
        }
      </div>

      <!-- Aggregate cards — computed from the VISIBLE (filtered) rows, so they always match the table.
           Every hour figure renders with EXACTLY two decimals (number:'1.2-2', trailing zeros included); the
           values themselves are already quantized to that grid (see summary() / WorktimeStatsCalculator). -->
      @if (overview()) {
        @let o = summary();
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <div class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <div class="text-xs font-medium text-gray-500">Gesamtstunden</div>
            <div class="mt-1 text-2xl font-bold text-gray-900"
                 data-testid="tile-total-hours">{{ o.totalHours | number:'1.2-2' }}</div>
          </div>
          <div class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <div class="text-xs font-medium text-gray-500">Schichten</div>
            <div class="mt-1 text-2xl font-bold text-gray-900">{{ o.totalShiftCount }}</div>
          </div>
          <div class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <div class="text-xs font-medium text-gray-500">Mitarbeiter:innen</div>
            <div class="mt-1 text-2xl font-bold text-gray-900">{{ o.employeeCount }}</div>
          </div>
          <div class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <div class="text-xs font-medium text-gray-500">Ø Std. / Mitarbeiter:in</div>
            <div class="mt-1 text-2xl font-bold text-gray-900"
                 data-testid="tile-avg-hours-employee">{{ o.averageHoursPerEmployee | number:'1.2-2' }}</div>
          </div>
          <div class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <div class="text-xs font-medium text-gray-500">Ø Std. / Monat</div>
            <div class="mt-1 text-2xl font-bold text-gray-900"
                 data-testid="tile-avg-hours-month">{{ o.averageHoursPerMonth | number:'1.2-2' }}</div>
          </div>
        </div>
      }

      <!-- States -->
      @if (loading()) {
        <div class="rounded-2xl border border-gray-100 bg-white p-8 text-center text-sm italic text-gray-500 shadow-sm"
             data-testid="loading">Wird geladen…</div>
      } @else if (error()) {
        <div class="rounded-2xl border border-red-100 bg-red-50 p-6 text-center text-sm text-red-700 shadow-sm"
             data-testid="error">
          Die Mitarbeiterliste konnte nicht geladen werden.
          <button type="button" (click)="reload()" class="ml-2 font-semibold underline">Erneut versuchen</button>
        </div>
      } @else if (rows().length === 0) {
        <div class="rounded-2xl border-2 border-dashed border-gray-100 p-10 text-center text-sm italic text-gray-500"
             data-testid="empty">Keine Mitarbeiter:innen für die gewählten Filter.</div>
      } @else {
        <!-- Desktop table. The first column is FROZEN (#389): it sticks to the wrapper's left edge while columns
             2+ slide behind it. Sticky cells are lifted out of the row's paint order, so they no longer inherit
             the row background — each pinned cell carries its own opaque background (bg-gray-50 in the head,
             !bg-white in the body) and re-applies the row hover via group / group-hover, or the hover would
             visibly stop at the freeze boundary.
             The two ! modifiers on the BODY cells are load-bearing, not style: this app loads Bootstrap 5
             alongside Tailwind, and Bootstrap ships .bg-white{...!important}. That beat the plain
             group-hover:bg-gray-50 (no !important) at any specificity, so the hovered row went gray from column
             2 on while the pinned Name cell stayed pure white — reported from the real app. !bg-white plus
             group-hover:!bg-gray-50 makes both sides !important, and .group:hover .group-hover\\:\\!bg-gray-50
             (0,2,0) then outranks .bg-white (0,1,0). bg-gray-50 in the head needs no such treatment: Bootstrap
             has no .bg-gray-50 (checked, not assumed).
             transition-colors on those same body cells is the SECOND half of that fix and is not optional. The
             non-pinned cells are transparent, so they simply show the <tr>'s background easing over its 150ms
             transition-colors; the pinned cells paint their OWN background, which inherits none of that motion,
             so column 1 snapped while the rest of the row faded ("the animation is missing on the first column").
             The cells now carry the identical property set / duration / easing as the row. No ! modifier needed
             here: Bootstrap defines no .transition-* utility at all (checked). The <th> deliberately has none —
             the header has no hover state to fade.
             The boundary itself is a positioned PSEUDO-ELEMENT, because
             this table is border-collapse: collapse and a cell there paints neither a border nor a box-shadow
             (both compute fine and render nothing — measured; see the cq-frozen-edge styles below). -->
        <!-- tabindex/role/aria-label: freezing a column only matters because this region really scrolls, and a
             scrollable region must be reachable by KEYBOARD alone (axe "scrollable-region-focusable", serious) —
             otherwise columns 2+ are unreachable without a pointer. role=group carries the accessible name;
             aria-label on a plain (generic) div would be a prohibited attribute instead.
             #tableWrapper is what the scroll-edge effect binds its passive scroll listener to (see scrolledX). -->
        <div class="relative hidden md:block">
        <!-- THE PINNED COLUMN HEADERS (#389), and the reason they are a CLONE rather than a sticky <thead>.
             overflow-x: auto on the wrapper below forces overflow-y to compute to auto as well (CSS Overflow 3),
             so that wrapper is a scroll container on BOTH axes and any sticky cell inside it pins to the WRAPPER,
             never to the page — measured, a naive sticky header simply scrolled away with the page, and neither
             overflow-y: clip nor hidden escapes it (both compute to hidden and the box stays the scrollport).
             Capping the wrapper's height WOULD pin it, at the price of a second vertical scrollbar, which was
             rejected. So the visible-when-scrolled header lives OUTSIDE the wrapper, where its nearest scrollport
             is the page.

             It is PURELY PRESENTATIONAL: aria-hidden, and every control in it is tabindex="-1" (an aria-hidden
             subtree containing a tabbable control is an axe "aria-hidden-focus" violation). The REAL <thead>
             below stays intact, in flow and semantic — it remains the only column-header source screen readers
             and keyboards ever see, so <th>/scope association with the body cells is untouched. The clone only
             fades in once the real header has scrolled out of view, so the two are never on screen together.
             h-0 keeps it out of the layout entirely; the inner viewport overflows it and does the clipping.

             Tell the two apart in the DOM by data-testid: the clone is [data-testid="employee-header-clone"] and
             is NOT inside [data-testid="employee-table-wrapper"]; the real header is. -->
        <div class="cq-pinned-head sticky z-30 h-0" [class.cq-pinned-head-on]="headerPinned()"
             aria-hidden="true" data-testid="employee-header-clone">
          <div class="overflow-hidden rounded-2xl border border-gray-100 bg-gray-50 shadow-sm" #pinnedHeadViewport>
            <table class="text-sm" [style.width.px]="pinnedHeadWidth()">
              <colgroup>
                @for (width of columnWidths(); track $index) {
                  <col [style.width.px]="width">
                }
              </colgroup>
              <thead>
                <tr class="whitespace-nowrap text-left text-xs uppercase tracking-wide text-gray-500">
                  @if (selectionMode()) {
                    <th class="sticky left-0 z-20 w-12 min-w-12 bg-gray-50 px-3 py-3">
                      <input type="checkbox" tabindex="-1" [ngModel]="allSelected()"
                             (ngModelChange)="selectAll($event)" name="pinned-select-all">
                    </th>
                  }
                  <th class="cq-frozen-edge sticky z-20 bg-gray-50 px-4 py-3 font-semibold"
                      [class.cq-frozen-edge-on]="scrolledX()"
                      [class.left-0]="!selectionMode()" [class.left-12]="selectionMode()">
                    <button type="button" tabindex="-1" (click)="setSort('name')"
                            class="uppercase">Name {{ sortMarker('name') }}</button>
                  </th>
                  <th class="px-4 py-3 font-semibold">Status</th>
                  <th class="hidden px-4 py-3 font-semibold lg:table-cell">
                    <button type="button" tabindex="-1" (click)="setSort('lastShift')"
                            class="uppercase">Letzter Dienst {{ sortMarker('lastShift') }}</button>
                  </th>
                  <th class="px-4 py-3 text-right font-semibold">
                    <button type="button" tabindex="-1" (click)="setSort('hours')"
                            class="uppercase">Stunden {{ sortMarker('hours') }}</button>
                  </th>
                  <th class="px-4 py-3 text-right font-semibold">Schichten</th>
                  <th class="hidden px-4 py-3 text-right font-semibold 2xl:table-cell">Ø Schicht</th>
                  <th class="hidden px-4 py-3 text-right font-semibold 2xl:table-cell">Ø Std./Monat</th>
                  <th class="hidden px-4 py-3 font-semibold lg:table-cell">Arbeitsverhältnis</th>
                  @if (canUseContactQuickLinks) {
                    <th class="px-3 py-3 font-semibold">Kontakt</th>
                  }
                  <th class="hidden px-3 py-3 font-semibold xl:table-cell">E-Mail</th>
                  <th class="px-4 py-3 font-semibold">Login</th>
                  <th class="px-4 py-3"></th>
                </tr>
              </thead>
            </table>
          </div>
        </div>
        <div class="cq-table-wrapper overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-sm"
             #tableWrapper
             data-testid="employee-table-wrapper" tabindex="0" role="group" aria-label="Mitarbeitertabelle">
          <table class="min-w-full divide-y divide-gray-100 text-sm" id="eaRelative" #realTable>
            <thead class="bg-gray-50" #realHead>
              <!-- whitespace-nowrap (inherited) keeps headers like "Ø Schicht" on one line; the table then
                   overflows into the wrapper's overflow-x-auto instead of the columns wrapping. -->
              <tr class="whitespace-nowrap text-left text-xs uppercase tracking-wide text-gray-500" #realHeadRow>
                @if (selectionMode()) {
                  <!-- Selection mode changes WHICH cell is first: the checkbox column takes over column 1 and is
                       pinned at left-0 with a fixed 3rem width so the name column can pin at exactly that offset
                       (left-12) — no gap, no overlap.
                       min-w-12 is LOAD-BEARING, not belt-and-braces: under table-layout: auto a plain width is
                       only a SUGGESTION and loses to content-based column distribution (measured: the cell
                       rendered 37px while the name column stayed pinned at the assumed 48px, opening an 11px
                       seam the scrolling columns showed through). min-width IS honoured by the auto layout
                       algorithm, so it is what actually holds the offset. NOTE: no backticks in this comment —
                       the template is a TS template literal and one would terminate it mid-markup (it did).
                       The stories' play() assert the seam is exactly 0px. -->
                  <th class="sticky left-0 z-20 w-12 min-w-12 bg-gray-50 px-3 py-3">
                    <input type="checkbox" [ngModel]="allSelected()" (ngModelChange)="selectAll($event)"
                           aria-label="Alle auswählen">
                  </th>
                }
                <!-- cq-frozen-edge only PAINTS once the wrapper is actually scrolled sideways (cq-frozen-edge-on,
                     driven by scrolledX): at rest there is nothing behind the column, so an edge there is just a
                     stray line. See the component styles below. -->
                <th class="cq-frozen-edge sticky z-20 bg-gray-50 px-4 py-3 font-semibold"
                    [class.cq-frozen-edge-on]="scrolledX()"
                    [class.left-0]="!selectionMode()" [class.left-12]="selectionMode()">
                  <button type="button" (click)="setSort('name')" class="uppercase">Name {{ sortMarker('name') }}</button>
                </th>
                <th class="px-4 py-3 font-semibold">Status</th>
                <th class="hidden px-4 py-3 font-semibold lg:table-cell">
                  <button type="button" (click)="setSort('lastShift')" class="uppercase">Letzter Dienst {{ sortMarker('lastShift') }}</button>
                </th>
                <th class="px-4 py-3 text-right font-semibold">
                  <button type="button" (click)="setSort('hours')" class="uppercase">Stunden {{ sortMarker('hours') }}</button>
                </th>
                <th class="px-4 py-3 text-right font-semibold">Schichten</th>
                <!-- The two derived averages sit at 2xl, not xl, and that is a MEASUREMENT rather than a taste
                     (#558). This table is wider than the viewport at every desktop width — at 1440 px the app
                     shell gives it a 1230 px box — so a column's breakpoint decides whether it is on screen or
                     only reachable by dragging the table sideways, which loses the frozen name column's
                     context. „Kontakt" is the one column here you PRESS, so it has to be inside that box; with
                     both averages at xl the columns to its left already came to 1235 px and it started one
                     pixel past the edge, which is the bug this replaced. Both numbers are second-order (a mean
                     of the two columns beside them) and both are already summarised in the aggregate cards
                     above the table, so they are the right pair to hold back to 1536 px. -->
                <th class="hidden px-4 py-3 text-right font-semibold 2xl:table-cell">Ø Schicht</th>
                <th class="hidden px-4 py-3 text-right font-semibold 2xl:table-cell">Ø Std./Monat</th>
                <th class="hidden px-4 py-3 font-semibold lg:table-cell">Arbeitsverhältnis</th>
                <!-- Kontakt (#558): the press-able channel, gated on the same four roles the endpoint is. A
                     session without them renders NO column at all rather than an empty one — a heading over
                     nothing reads as "this person has no contacts", which is a different (and wrong) fact.

                     It sits in the slot the old „Telegram / Email" column held, and that placement is the
                     whole point of the merge: ONE column carries every way to reach somebody, so the table
                     gains no column for the feature. What used to be „Telegram / Email" is now „E-Mail"
                     alone — the legacy, unconfirmed Telegram handle moved INTO this cell as the fallback
                     rendering (see the body cell), because a second telegram column beside a Telegram chip
                     is the same fact twice, in two places that disagree the moment consent is withdrawn. -->
                @if (canUseContactQuickLinks) {
                  <th class="px-3 py-3 font-semibold">Kontakt</th>
                }
                <th class="hidden px-3 py-3 font-semibold xl:table-cell">E-Mail</th>
                <th class="px-4 py-3 font-semibold">Login</th>
                <th class="px-4 py-3"><span class="sr-only">Aktionen</span></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              @for (r of rows(); track r.employee.id) {
                <!-- group: the pinned cells below carry their own opaque background, so the row's hover:bg-gray-50
                     cannot reach them — group-hover re-applies it and the highlight stays continuous across the
                     freeze boundary. opacity-70 stays on the <tr>: opacity composites the whole row as one group,
                     so the dimming reads through the pinned cell without letting the scrolling columns bleed
                     through its background. -->
                <!-- Both animate bindings answer '' unless this employee id is currently marked as animating,
                     and Angular reads '' as "no animation" — which is what keeps filter / multi-select / sort
                     changes INSTANT while a genuine data mutation (add, end employment, rehire) collapses or
                     expands its row. See markRowAnimating() and rowLeaveAnimation()'s note on lazy evaluation. -->
                <tr class="group transition-colors hover:bg-gray-50" [class.opacity-70]="!r.employee.isEmployed"
                    [animate.enter]="rowEnterAnimation(r.employee.id)"
                    [animate.leave]="rowLeaveAnimation(r.employee.id)">
                  @if (selectionMode()) {
                    <!-- min-w-12 mirrors the header cell: it is what actually pins the 3rem column width the
                         name column's left-12 offset assumes (see the <th> note above). -->
                    <td class="sticky left-0 z-10 w-12 min-w-12 !bg-white px-3 py-3 transition-colors group-hover:!bg-gray-50">
                      <div class="cq-collapse">
                        <input type="checkbox" [ngModel]="isSelected(r.employee.id)"
                               (ngModelChange)="setSelected(r.employee.id, $event)"
                               [attr.aria-label]="'Auswählen: ' + fullName(r.employee)">
                      </div>
                    </td>
                  }
                  <td class="cq-frozen-edge sticky z-10 whitespace-nowrap !bg-white px-4 py-3 font-medium transition-colors group-hover:!bg-gray-50"
                      [class.cq-frozen-edge-on]="scrolledX()"
                      [class.left-0]="!selectionMode()" [class.left-12]="selectionMode()">
                    <!-- cq-collapse is inert at rest (a plain div). Only while the row carries cq-anim-in /
                         cq-anim-out does it become the single-track grid whose 1fr <-> 0fr shrinks the cell
                         content — a <tr> cannot have its own height transitioned. See the styles below. -->
                    <div class="cq-collapse"><div class="flex items-center gap-2">
                      <a [routerLink]="['/' + Routes.CompanyEmployeeDetails]" [queryParams]="{ id: r.employee.id }"
                         class="text-theme-primary-700 hover:text-theme-primary-900 hover:underline">{{ fullName(r.employee) }}</a>
                      <button type="button" (click)="openAttributePopup($event, r.employee)" id="eaToggle"
                              [attr.aria-label]="'Attribute anzeigen: ' + fullName(r.employee)"
                              class="text-gray-400 hover:text-gray-600">
                        <svg class="h-4 w-4" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" fill="currentColor"/>
                        </svg>
                      </button>
                      <span class="hidden gap-1 text-xs text-gray-500 md:flex">
                        @for (attr of r.employee.employeeAttributes; track attr.id) {
                          @if (attr.shortName !== '') {
                            <span class="rounded border border-gray-300 px-1">{{ attr.shortName }}</span>
                          }
                        }
                      </span>
                    </div></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="cq-collapse">
                    <span class="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
                          [class.bg-green-50]="statusOf(r.employee) === 'employed'"
                          [class.text-green-700]="statusOf(r.employee) === 'employed'"
                          [class.bg-amber-50]="statusOf(r.employee) === 'upcoming'"
                          [class.text-amber-900]="statusOf(r.employee) === 'upcoming'"
                          [class.bg-gray-100]="statusOf(r.employee) === 'former'"
                          [class.text-gray-700]="statusOf(r.employee) === 'former'">
                      {{ statusLabel(r.employee) }}
                    </span>
                    </div>
                  </td>
                  <td class="hidden whitespace-nowrap px-4 py-3 text-gray-500 lg:table-cell">
                    <div class="cq-collapse">{{ r.employee.lastShift ? ((r.employee.lastShift | date:'dd.MM.yyyy, HH:mm') + ' Uhr') : '–' }}</div>
                  </td>
                  <!-- tabular-nums + the fixed two decimals keep the numeric columns aligned across rows. -->
                  <td class="px-4 py-3 text-right tabular-nums"
                      data-testid="row-hours"><div class="cq-collapse">{{ r.stats ? (r.stats.hoursWorked | number:'1.2-2') : '–' }}</div></td>
                  <td class="px-4 py-3 text-right tabular-nums"><div class="cq-collapse">{{ r.stats?.shiftCount ?? '–' }}</div></td>
                  <td class="hidden px-4 py-3 text-right tabular-nums 2xl:table-cell"
                      data-testid="row-avg-shift"><div class="cq-collapse">{{ r.stats ? (r.stats.averageShiftLength | number:'1.2-2') : '–' }}</div></td>
                  <td class="hidden px-4 py-3 text-right tabular-nums 2xl:table-cell"
                      data-testid="row-avg-month"><div class="cq-collapse">{{ r.stats ? (r.stats.averageHoursPerMonth | number:'1.2-2') : '–' }}</div></td>
                  <td class="hidden whitespace-nowrap px-4 py-3 lg:table-cell">
                    <div class="cq-collapse">
                    @if (relationshipOf(r); as rel) {
                      <button type="button" (click)="selectRelationship(rel.id)"
                              class="text-theme-primary-700 hover:text-theme-primary-900 hover:underline">{{ rel.name }}</button>
                    } @else {
                      <span class="text-gray-400">–</span>
                    }
                    </div>
                  </td>
                  @if (canUseContactQuickLinks) {
                    <td class="px-3 py-3">
                      <!-- The extra inner div is what a cq-collapse needs when a cell can render more than one
                           child: the collapsing grid has exactly ONE explicit row, and a second child lands in
                           an implicit auto row that never shrinks.
                           NO width cap and NO truncation here, deliberately — an earlier pass capped the cell
                           at 6 rem to buy table width and it was wrong: a handle is an IDENTIFIER, and half of
                           „UID: 6984739009" is not a shorter identifier, it is a useless one. A manager cannot
                           read it off the screen, cannot check it against a phone, and a tooltip does not help
                           somebody scanning eighty rows. The column is content-sized instead, and the width it
                           costs is paid for by the two Ø columns moving to 2xl (see the header note).
                           flex-wrap is the other half: this column holds ONE channel today, and the whole point
                           of the merge is that it holds every future one. A second chip must fall onto a second
                           LINE — a row is free to grow downwards, and the table is not free to grow sideways.
                           Never flex-nowrap and never overflow-hidden here: both turn "two channels" into
                           "one channel and something you cannot see". The chip host carries
                           inline-flex + max-w-full + min-w-0 (5f5008c19) so a chip yields to a narrow column on
                           its own — which is why this cell needs no cap to stay contained.
                           NOTE: no backticks anywhere in this comment — the template is a TS template literal
                           and one would terminate it mid-markup (it did, on the first pass here). -->
                      <div class="cq-collapse"><div class="flex flex-wrap items-center gap-1"
                                                    data-testid="contact-cell">
                        @for (quick of contactLinksFor(r.employee.id); track quick.channel) {
                          <cqme-contact-chip [link]="quick" [channelLabel]="contactChannelLabel(quick.channel)"/>
                        } @empty {
                          <!-- No consented channel. The legacy handle from the employee profile is still worth
                               showing — it is how this roster worked before #558 — but it is NOT the same fact
                               as a chip: nobody consented to it and nothing has proven it reachable. So it is
                               plain muted text rather than a link or a chip, and says so on hover. The handle
                               still leads the tooltip, even though the cell no longer clips it, so the hover
                               explanation names WHAT it is qualifying. -->
                          @if (legacyTelegramHandle(r.employee); as legacy) {
                            <span class="text-xs text-gray-400" data-testid="legacy-telegram"
                                  [title]="legacy + '. Nicht bestätigte Angabe aus dem Mitarbeiterprofil.'">{{ legacy }}</span>
                          } @else {
                            <span class="text-gray-400">–</span>
                          }
                        }
                      </div></div>
                    </td>
                  }
                  <td class="hidden px-3 py-3 text-gray-500 xl:table-cell">
                    <!-- E-Mail only since #558 — the Telegram half of the old „Telegram / Email" column moved
                         into „Kontakt". This one IS capped and truncated, unlike „Kontakt" beside it, and the
                         difference is the point: an e-mail address stays recognisable from its first dozen
                         characters and is one click away as a mailto: link regardless, whereas a truncated handle
                         or Telegram UID identifies nobody. The full address is in the title. -->
                    <div class="cq-collapse"><div class="max-w-[6rem] truncate">
                    @if (r.employee.emailAddress) {
                      <a [title]="r.employee.emailAddress" href="mailto:{{ r.employee.emailAddress }}">{{ r.employee.emailAddress }}</a>
                    } @else {
                      <span class="text-gray-400">–</span>
                    }
                    </div></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="cq-collapse">
                    <input type="checkbox" [ngModel]="r.employee.canLogin" (ngModelChange)="setCanLogin(r.employee, $event)"
                           [disabled]="!PermissionService.CheckPermission(PermissionService.Company_Employee_AllowEmployeeLogin())"
                           [class.opacity-50]="!PermissionService.CheckPermission(PermissionService.Company_Employee_AllowEmployeeLogin())"
                           [attr.aria-label]="'Login erlaubt: ' + fullName(r.employee)">
                    </div>
                  </td>
                  <td class="whitespace-nowrap px-4 py-3 text-right">
                    <div class="cq-collapse"><div class="inline-flex items-center gap-3">
                      @if (PermissionService.CheckPermission(PermissionService.Company_Employee_Get())) {
                        <button type="button" (click)="redirectToSettings(r.employee.id)"
                                [attr.aria-label]="'Bearbeiten: ' + fullName(r.employee)"
                                class="text-gray-600 hover:text-gray-800">
                          <svg class="h-5 w-5" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                            <path d="m13.498.795.149-.149a1.207 1.207 0 1 1 1.707 1.708l-.149.148a1.5 1.5 0 0 1-.059 2.059L4.854 14.854a.5.5 0 0 1-.233.131l-4 1a.5.5 0 0 1-.606-.606l1-4a.5.5 0 0 1 .131-.232l9.642-9.642a.5.5 0 0 0-.642.056L6.854 4.854a.5.5 0 1 1-.708-.708L9.44.854A1.5 1.5 0 0 1 11.5.796a1.5 1.5 0 0 1 1.998-.001zm-.644.766a.5.5 0 0 0-.707 0L1.95 11.756l-.764 3.057 3.057-.764L14.44 3.854a.5.5 0 0 0 0-.708l-1.585-1.585z" fill="currentColor"/>
                          </svg>
                        </button>
                      }
                      @if (PermissionService.CheckPermission(PermissionService.Company_Employee_Delete())) {
                        <!-- Upcoming (#864) gets neither action: "Wieder einstellen" would be refused (a period
                             is already open) and "Beenden" today would end before the start. -->
                        @if (statusOf(r.employee) === 'employed') {
                          <button type="button" (click)="openLifecycleDialog('end', r.employee)"
                                  data-testid="end-employment" title="Beschäftigung beenden"
                                  class="font-medium text-red-600 hover:text-red-800">Beenden</button>
                        } @else if (statusOf(r.employee) === 'former') {
                          <button type="button" (click)="openLifecycleDialog('rehire', r.employee)"
                                  data-testid="rehire" title="Wieder einstellen"
                                  class="font-medium text-theme-primary-700 hover:text-theme-primary-900">Wieder einstellen</button>
                        }
                      }
                    </div></div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <!-- THE PINNED HORIZONTAL SCROLLBAR (#458) — the deliberate mirror of the header clone above, and a
             clone for the SAME measured reason. A scroll container paints its horizontal bar at the bottom of
             its OWN box, which here is the bottom of the whole table; with a realistic roster that is far below
             the fold, so the only sideways-scroll control is unreachable until the page is scrolled to the very
             end. Sticking the real bar is not an option: overflow-x: auto on the wrapper forces overflow-y to
             compute to auto too (CSS Overflow 3), so the wrapper is a scrollport on BOTH axes and anything
             sticky INSIDE it pins to the wrapper, never to the page — the identical trap documented for the
             header. Capping the wrapper's height would pin it, at the price of a second vertical scrollbar,
             which #389 already rejected. So this bar lives OUTSIDE the wrapper, as its sibling, where its
             nearest scrollport is the page. DO NOT "simplify" it into a sticky child of the wrapper: it will
             compile, look plausible, and silently pin to the wrong box.

             It is a real scroll container with nothing in it but a spacer as wide as the table (that spacer is
             the only reason it has anything to scroll), kept in two-way sync with the wrapper — the header
             clone is one-way because nobody drags a header, whereas dragging THIS is the entire point.

             NOTE: no backticks in this comment — the template is a TS template literal and one would terminate
             it mid-markup (it did, on the first pass here).

             It sits inside the same hidden md:block box as the table, so it is absent at phone widths and can
             never appear over the card list below. h-0 keeps it out of the flow exactly like the header clone;
             unlike the header clone its inner viewport is absolutely positioned, because a zero-height box
             resting at bottom: 0 has its top edge ON the scrollport's bottom edge, so a static child would flow
             downwards and land entirely below the fold. Anchoring the viewport's BOTTOM to the box makes it
             grow upwards into view instead.

             Purely presentational: aria-hidden, and it contains nothing tabbable (an aria-hidden subtree with a
             tabbable control is an axe "aria-hidden-focus" violation — the header clone hit exactly this). The
             real wrapper keeps its own tabindex/role/aria-label and stays the keyboard-reachable scroll region.
             Tell them apart in the DOM by data-testid: [data-testid="employee-scrollbar-clone"] is NOT inside
             [data-testid="employee-table-wrapper"]. -->
        <div class="cq-pinned-scroll sticky z-30 h-0"
             [class.cq-pinned-scroll-on]="scrollbarPinned()"
             aria-hidden="true" data-testid="employee-scrollbar-clone">
          <div class="cq-pinned-scroll-viewport absolute inset-x-0 bottom-0 overflow-x-auto border-t border-gray-200 bg-gray-50"
               #pinnedScrollViewport>
            <!-- The 1px spacer IS the scrollable content. Its width is the same measured table width the header
                 clone uses, so both clones and the wrapper share one scroll range and cannot drift. The left and
                 right borders above mirror the wrapper's, which is what keeps the two clientWidths — and
                 therefore the two maximum scrollLeft values — identical. -->
            <div class="h-px" [style.width.px]="pinnedHeadWidth()"></div>
          </div>
        </div>
        </div>

        <!-- Mobile cards. data-testid: the phone-width E2E asserts the card list renders (and that no sticky
             header clone does) below md; keying that on the md:hidden utility class would couple the test to a
             Tailwind class name that a restyle could change without changing behaviour. -->
        <ul class="grid grid-cols-1 gap-4 md:hidden" data-testid="employee-card-list">
          @for (r of rows(); track r.employee.id) {
            <!-- Same conditional enter/leave contract as the desktop rows. A card is a block box, so ONE
                 cq-collapse wrapper around its whole body is enough — no per-cell wrappers needed. -->
            <li class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm" [class.opacity-80]="!r.employee.isEmployed"
                [animate.enter]="rowEnterAnimation(r.employee.id)"
                [animate.leave]="rowLeaveAnimation(r.employee.id)">
              <div class="cq-collapse"><div>
              <div class="flex items-center justify-between gap-2">
                <div class="flex min-w-0 items-center gap-2">
                  @if (selectionMode()) {
                    <input type="checkbox" [ngModel]="isSelected(r.employee.id)"
                           (ngModelChange)="setSelected(r.employee.id, $event)"
                           [attr.aria-label]="'Auswählen: ' + fullName(r.employee)">
                  }
                  <a [routerLink]="['/' + Routes.CompanyEmployeeDetails]" [queryParams]="{ id: r.employee.id }"
                     class="truncate font-semibold text-theme-primary-700 hover:underline">{{ fullName(r.employee) }}</a>
                </div>
                <span class="inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
                      [class.bg-green-50]="statusOf(r.employee) === 'employed'"
                      [class.text-green-700]="statusOf(r.employee) === 'employed'"
                      [class.bg-amber-50]="statusOf(r.employee) === 'upcoming'"
                      [class.text-amber-900]="statusOf(r.employee) === 'upcoming'"
                      [class.bg-gray-100]="statusOf(r.employee) === 'former'"
                      [class.text-gray-700]="statusOf(r.employee) === 'former'">{{ statusLabel(r.employee) }}</span>
              </div>
              <dl class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div><dt class="text-xs text-gray-400">Stunden</dt><dd class="font-medium tabular-nums text-gray-900"
                     data-testid="card-hours">{{ r.stats ? (r.stats.hoursWorked | number:'1.2-2') : '–' }}</dd></div>
                <div><dt class="text-xs text-gray-400">Schichten</dt><dd class="font-medium text-gray-900">{{ r.stats?.shiftCount ?? '–' }}</dd></div>
                <div><dt class="text-xs text-gray-400">Letzter Dienst</dt><dd class="text-gray-600">{{ r.employee.lastShift ? (r.employee.lastShift | date:'dd.MM.yy') : '–' }}</dd></div>
                <div>
                  <dt class="text-xs text-gray-400">Arbeitsverhältnis</dt>
                  <dd>
                    @if (relationshipOf(r); as rel) {
                      <button type="button" (click)="selectRelationship(rel.id)"
                              class="text-theme-primary-700 hover:underline">{{ rel.name }}</button>
                    } @else {
                      <span class="text-gray-400">–</span>
                    }
                  </dd>
                </div>
                @if (r.employee.emailAddress) {
                  <div class="col-span-2"><dt class="text-xs text-gray-400">E-Mail</dt>
                    <dd class="truncate"><a href="mailto:{{ r.employee.emailAddress }}">{{ r.employee.emailAddress }}</a></dd></div>
                }
                <!-- Below md there are no column headings, so the card restates the label as the <dt>. Rendered
                     only when this session may have quick links AND this person has something to show — a chip
                     or the legacy profile handle. An empty "Kontakt" row on every card would cost a line per
                     employee to say nothing, so a card with neither omits the row entirely (the desktop table
                     still owes its row a cell, and shows an en dash there).
                     The content rules are the table's, verbatim: chips first, the unconfirmed profile handle as
                     muted plain text when there is no consented channel. -->
                @if (canUseContactQuickLinks && (contactLinksFor(r.employee.id).length > 0 || legacyTelegramHandle(r.employee))) {
                  <div class="col-span-2"><dt class="text-xs text-gray-400">Kontakt</dt>
                    <!-- Same two rules as the table cell, and the card is where they BITE: this <dd> sits in a
                         fixed grid column on a 390 px phone, so a second channel has nowhere to go sideways and
                         wraps for real. No truncation either — the handle is an identifier, and the card has
                         the vertical room the table row is grudging about. -->
                    <dd class="mt-0.5 flex flex-wrap items-center gap-1" data-testid="contact-cell">
                      @for (quick of contactLinksFor(r.employee.id); track quick.channel) {
                        <cqme-contact-chip [link]="quick" [channelLabel]="contactChannelLabel(quick.channel)"/>
                      } @empty {
                        @if (legacyTelegramHandle(r.employee); as legacy) {
                          <span class="text-xs text-gray-400" data-testid="legacy-telegram"
                                [title]="legacy + '. Nicht bestätigte Angabe aus dem Mitarbeiterprofil.'">{{ legacy }}</span>
                        }
                      }
                    </dd></div>
                }
              </dl>
              <div class="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 text-sm">
                <label class="inline-flex items-center gap-2 text-gray-600">
                  <input type="checkbox" [ngModel]="r.employee.canLogin" (ngModelChange)="setCanLogin(r.employee, $event)"
                         [disabled]="!PermissionService.CheckPermission(PermissionService.Company_Employee_AllowEmployeeLogin())">
                  Login erlaubt
                </label>
                <div class="flex items-center gap-3">
                  @if (PermissionService.CheckPermission(PermissionService.Company_Employee_Get())) {
                    <button type="button" (click)="redirectToSettings(r.employee.id)"
                            class="font-medium text-gray-600 hover:text-gray-800">Bearbeiten</button>
                  }
                  @if (PermissionService.CheckPermission(PermissionService.Company_Employee_Delete())) {
                    @if (statusOf(r.employee) === 'employed') {
                      <button type="button" (click)="openLifecycleDialog('end', r.employee)"
                              class="font-medium text-red-600 hover:text-red-800">Beenden</button>
                    } @else if (statusOf(r.employee) === 'former') {
                      <button type="button" (click)="openLifecycleDialog('rehire', r.employee)"
                              class="font-medium text-theme-primary-700 hover:text-theme-primary-900">Wieder einstellen</button>
                    }
                  }
                </div>
              </div>
              </div></div>
            </li>
          }
        </ul>
      }
    </div>

    <!-- End-employment / rehire confirmation dialog -->
    @if (lifecycleDialog(); as dlg) {
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4" data-testid="lifecycle-dialog"
           (click)="closeLifecycleDialog()">
        <div class="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-6 shadow-xl" role="dialog"
             aria-modal="true" [attr.aria-label]="dlg.mode === 'end' ? 'Beschäftigung beenden' : 'Wieder einstellen'"
             (click)="$event.stopPropagation()">
          <h2 class="text-lg font-bold text-gray-900">
            {{ dlg.mode === 'end' ? 'Beschäftigung beenden' : 'Wieder einstellen' }}
          </h2>
          <p class="mt-2 text-sm text-gray-500">
            @if (dlg.mode === 'end') {
              Die Beschäftigung von <span class="font-semibold">{{ fullName(dlg.employee) }}</span> wird zum gewählten
              Datum beendet. Es werden keine Daten gelöscht — alle Schichten, Verkäufe und Statistiken bleiben
              erhalten und die Person erscheint weiterhin unter „Ehemalig“.
            } @else {
              <span class="font-semibold">{{ fullName(dlg.employee) }}</span> wird zum gewählten Datum wieder
              eingestellt. Die bisherige Historie bleibt als frühere Beschäftigung erhalten.
            }
          </p>
          <label class="mt-4 block text-sm font-medium text-gray-900" for="lifecycle-date">
            {{ dlg.mode === 'end' ? 'Beschäftigungsende' : 'Eintrittsdatum' }}
          </label>
          <input [ngModel]="dlg.dateStr" (ngModelChange)="setDialogDate($event)" id="lifecycle-date" type="date"
                 class="mt-1 h-10 w-full rounded-xl border border-gray-200 px-3 text-sm focus:border-theme-primary-500 focus:outline-none focus:ring-1 focus:ring-theme-primary-500">
          <div class="mt-6 flex justify-end gap-3">
            <button type="button" (click)="closeLifecycleDialog()"
                    class="rounded-xl px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100">Abbrechen</button>
            <button type="button" (click)="confirmLifecycleDialog()" data-testid="lifecycle-confirm"
                    [class.bg-red-600]="dlg.mode === 'end'" [class.hover:bg-red-700]="dlg.mode === 'end'"
                    [class.bg-theme-primary-700]="dlg.mode === 'rehire'" [class.hover:bg-theme-primary-900]="dlg.mode === 'rehire'"
                    class="rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm">
              {{ dlg.mode === 'end' ? 'Beschäftigung beenden' : 'Wieder einstellen' }}
            </button>
          </div>
        </div>
      </div>
    }

    <app-employee-attribute-popup [(employee)]="popupEmployee" [(visible)]="attributePopupVisible"
                                  [mouseEvent]="mouseEvent" [relative]="alignment.MainWindow" [useRelativeY]="true"
                                  [redirectPath]="[ '/' + Routes.CompanyEmployee ]"></app-employee-attribute-popup>

    <app-employment-relationship-popup [visible]="relationshipPopupVisible"
                                       (visibleChange)="onRelationshipPopupVisibleChange($event)"
                                       [changeIds]="selectedIds()"></app-employment-relationship-popup>
  `,
  // The frozen column's edge, the pinned header clone and the pinned horizontal scrollbar clone are the
  // SHARED house pattern and live in _ui/tables/pinned-table.css beside the controller that drives them.
  // Only the row enter/leave collapse is specific to this screen and stays inline below. Both are documented
  // in docs/articles/frontend/style-guide.md ("Frozen first column in a horizontally scrolling data table").
  styleUrls: ['../../../_ui/tables/pinned-table.css'],
  styles: [`
    /* --- Row enter / leave: a real collapse, driven by animate.enter / animate.leave --------------------------
       A <tr> cannot have its own height transitioned, and display: block on one destroys the table layout — so
       the CELL CONTENTS have to shrink. Every <td> (and the whole card body on phones) carries a .cq-collapse
       wrapper which is INERT at rest and only becomes a one-track grid while the row animates; the track goes
       1fr <-> 0fr and overflow: hidden clips what no longer fits. minmax(0, …) is load-bearing: a bare 0fr
       track still has an AUTO minimum, so the row stops at content height instead of collapsing (measured:
       46px -> 22px instead of 46px -> 1px).
       A .cq-collapse must wrap exactly ONE child — a second one lands in an implicit auto row that never shrinks.
       The <td>'s own block padding is animated alongside, or the row would bottom out at 24px of padding.
       The <tr>/<li> ALSO animates its own opacity, and that is not only decoration: Angular decides how long to
       keep a leaving node by reading el.getAnimations() on the element ITSELF, and descendant animations do not
       count. No animation on the row = the node is dropped on the next frame.
       The breakpoint gating below is NOT cosmetic — it is what keeps the DOM from leaking. Only ONE of the two
       lists is rendered at a time (the table is md:block, the cards are md:hidden), and a CSS animation does not
       run on an element with no box. Angular still sees the declared animation in the computed style, waits for
       an animationend that can never fire, and NEVER removes the node: measured at a 741px viewport, a leaving
       <tr> stayed in the DOM indefinitely with cq-anim-out on it. Declaring each list's animation only at the
       width where that list is visible means the hidden one has no animation at all and Angular drops it on the
       next frame — which is also exactly the behaviour we want there.
       768px is Tailwind's md.
       NOTE: no backticks anywhere in this stylesheet — it is a TS template literal and one would terminate it
       mid-block, exactly as in the template above. */
    @media (min-width: 768px) {
      tr.cq-anim-in .cq-collapse,
      tr.cq-anim-out .cq-collapse {
        display: grid;
        grid-template-rows: minmax(0, 1fr);
        overflow: hidden;
      }

      tr.cq-anim-in .cq-collapse { animation: cq-collapse-in 200ms ease-out; }
      tr.cq-anim-out .cq-collapse { animation: cq-collapse-out 200ms ease-in forwards; }

      tr.cq-anim-in { animation: cq-row-in 200ms ease-out; }
      tr.cq-anim-out { animation: cq-row-out 200ms ease-in forwards; }
      tr.cq-anim-in > td { animation: cq-cell-pad-in 200ms ease-out; }
      tr.cq-anim-out > td { animation: cq-cell-pad-out 200ms ease-in forwards; }

      /* The card list is the hidden one at this width — see the note below on why silence is not enough. */
      li.cq-anim-in,
      li.cq-anim-out { animation: none !important; transition: none !important; }
    }

    @media (max-width: 767px) {
      /* Mirror image, and NOT redundant: when Angular finds no running animation it falls back to the computed
         style, and there it accepts a TRANSITION as the thing to wait for. The rows carry Tailwind's
         transition-colors (150ms) for their hover, so a hidden leaving row still looked "animated" and waited
         for a transitionend that no property change would ever produce — the node stayed in the DOM for good.
         Zeroing both is what actually lets Angular drop it on the next frame. */
      tr.cq-anim-in,
      tr.cq-anim-out { animation: none !important; transition: none !important; }

      li.cq-anim-in .cq-collapse,
      li.cq-anim-out .cq-collapse {
        display: grid;
        grid-template-rows: minmax(0, 1fr);
        overflow: hidden;
      }

      li.cq-anim-in .cq-collapse { animation: cq-collapse-in 200ms ease-out; }
      li.cq-anim-out .cq-collapse { animation: cq-collapse-out 200ms ease-in forwards; }

      li.cq-anim-in { animation: cq-card-in 200ms ease-out; }
      li.cq-anim-out { animation: cq-card-out 200ms ease-in forwards; }
    }

    /* Every keyframe list below states only ONE end; the other end is the element's own computed style, so none
       of these hardcode the Tailwind padding/border values they animate away from. */
    @keyframes cq-collapse-in { from { grid-template-rows: minmax(0, 0fr); } }
    @keyframes cq-collapse-out { to { grid-template-rows: minmax(0, 0fr); } }
    @keyframes cq-row-in { from { opacity: 0; } }
    @keyframes cq-row-out { to { opacity: 0; } }
    @keyframes cq-cell-pad-in { from { padding-top: 0; padding-bottom: 0; } }
    @keyframes cq-cell-pad-out { to { padding-top: 0; padding-bottom: 0; } }

    @keyframes cq-card-in {
      from { opacity: 0; padding-top: 0; padding-bottom: 0; border-top-width: 0; border-bottom-width: 0; }
    }

    @keyframes cq-card-out {
      to { opacity: 0; padding-top: 0; padding-bottom: 0; border-top-width: 0; border-bottom-width: 0; }
    }

    /* Reduced motion is an accessibility REQUIREMENT here, not a nicety: rows appear and disappear instantly,
       with no height and no opacity animation. The PRIMARY guard is markRowAnimating(), which refuses to mark
       the row at all under the media feature, so these classes are never applied in the first place; this block
       is the second line of defence. Note transition: none alongside animation: none — without it Angular's
       computed-style fallback latches onto the row's 150ms transition-colors and waits for a transitionend that
       never comes, and a leaving row is never removed at all (measured). !important is deliberate too: the rules
       above are more specific (tr.cq-anim-out beats .cq-anim-out), and without it the opacity kept animating
       while only the height stopped (also measured). */
    @media (prefers-reduced-motion: reduce) {
      .cq-anim-in,
      .cq-anim-out { animation: none !important; transition: none !important; }

      tr.cq-anim-in > td,
      tr.cq-anim-out > td,
      .cq-anim-in .cq-collapse,
      .cq-anim-out .cq-collapse { animation: none !important; }

      .cq-anim-in .cq-collapse,
      .cq-anim-out .cq-collapse { display: block !important; overflow: visible !important; }

      .cq-frozen-edge::after { transition: none; }
    }
  `]
})
export class EmployeesComponent implements OnInit {
  private readonly statsHttp = inject(WorktimeStatsHttpService);
  private readonly employeeHttp = inject(EmployeeHttpService);
  private readonly relationshipHttp = inject(EmploymentRelationshipHttpService);
  /** Issues the employee-link invitation offered on the create form (#279). */
  private readonly grantHttp = inject(GrantHttpService);
  /** Reads the „Kontakt" column's chips, batched + cached across filter changes (#558). */
  private readonly contactQuickLinkHttp = inject(ContactQuickLinkHttpService);
  private readonly employeeService = inject(EmployeeService);
  private readonly alerts = inject(GlobalAlertService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly PermissionService = inject(PermissionService);
  // The app-shell scroll container (#relative). The attribute popup positions itself relative to it so it stays
  // aligned to the clicked row even after the page is scrolled (see the useRelativeY binding below).
  protected readonly alignment = inject(AlignmentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  /** The `overflow-x-auto` scroll container. Absent while the loading / error / empty branch is showing. */
  private readonly tableWrapper = viewChild<ElementRef<HTMLElement>>('tableWrapper');
  /** The REAL header — the width source for the presentational clone, and the trigger for showing it. */
  private readonly realHead = viewChild<ElementRef<HTMLElement>>('realHead');
  private readonly realHeadRow = viewChild<ElementRef<HTMLElement>>('realHeadRow');
  private readonly realTable = viewChild<ElementRef<HTMLElement>>('realTable');
  /** The clone's clipping box, whose scrollLeft mirrors the wrapper's. */
  private readonly pinnedHeadViewport = viewChild<ElementRef<HTMLElement>>('pinnedHeadViewport');
  /**
   * The pinned scrollbar clone's own scroll container (#458). Unlike the header clone's viewport this one is a
   * REAL `overflow-x: auto` box the user can drag, so its scrollLeft is both mirrored from and mirrored onto the
   * wrapper's.
   */
  private readonly pinnedScrollViewport = viewChild<ElementRef<HTMLElement>>('pinnedScrollViewport');

  /**
   * The shared floating-header mechanics (#389 header clone, #458 scrollbar clone), extracted to
   * {@link PinnedTableController} for #558 so the Pflicht-Kontakte screen runs the same code rather than a
   * second copy of it. The five signals below are ITS signals, re-exposed under their historical names
   * because this component's template and specs address them directly.
   */
  protected readonly pinnedTable = new PinnedTableController(this.ngZone, () => ({
    wrapper: this.tableWrapper()?.nativeElement,
    realHead: this.realHead()?.nativeElement,
    realHeadRow: this.realHeadRow()?.nativeElement,
    realTable: this.realTable()?.nativeElement,
    headViewport: this.pinnedHeadViewport()?.nativeElement,
    scrollViewport: this.pinnedScrollViewport()?.nativeElement
  }));

  /** Whether the table is scrolled horizontally at all — drives the frozen column's edge. */
  protected readonly scrolledX = this.pinnedTable.scrolledX;
  /** Whether the presentational header clone is showing, i.e. the real header has scrolled out of view. */
  protected readonly headerPinned = this.pinnedTable.headerPinned;
  /** Whether the pinned horizontal scrollbar clone is showing (#458). */
  protected readonly scrollbarPinned = this.pinnedTable.scrollbarPinned;
  /** Per-column widths measured off the REAL header, and their total. A hidden column measures 0, and stays 0. */
  protected readonly columnWidths = this.pinnedTable.columnWidths;
  protected readonly pinnedHeadWidth = this.pinnedTable.pinnedHeadWidth;


  /**
   * Employee ids whose row may animate its insertion / removal, i.e. the ones a DATA MUTATION just touched
   * (add, end employment, rehire). Everything else — the status segments, the two multi-selects, the date range,
   * column sorting — inserts and removes rows instantly, because those ids are never in here.
   *
   * A plain Set, not a signal, on purpose: nothing reads it during change detection. The two `animate.*`
   * bindings that do read it are resolved by Angular at the exact moment a node is inserted or removed.
   */
  private readonly animatingIds = new Set<string>();
  private readonly animationTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Fires a stats (worktime overview) refresh. Piped through switchMap so a rapid burst of date-picker changes
   * (a native date input emits many intermediate values) only ever applies the LATEST range's response — a
   * slower earlier request is cancelled and can never overwrite the current one.
   */
  private readonly statsTrigger = new Subject<void>();

  protected readonly Routes = AppRoutes;
  protected readonly ServerEndpoints = ServerEndpoints;
  protected readonly contactChannelLabel = contactChannelLabel;

  /**
   * Whether this session may see the „Kontakt" column at all (#558) — `ShardAdmin` / `CompanyOwner` /
   * `PlatformOwner` / `ShiftLead`, exactly what the endpoint gates on.
   *
   * Read ONCE, into a plain field, rather than called from the template: the roster re-renders on every
   * keystroke of the search and every sort click, and a permission answer that cannot change without a full
   * page reload has no business being recomputed on each of them. It is also what the loader below checks, so
   * the column and the request can never disagree about whether this session has quick links.
   */
  protected readonly canUseContactQuickLinks = this.PermissionService.CanUseContactQuickLinks();

  /**
   * The consented channels of every employee the page has asked about, keyed by employee id.
   *
   * Accumulated rather than replaced: `rows()` narrows as filters change, and dropping the ids that fell out
   * of view would re-request them the moment the filter is widened again — which, on a search box, is every
   * backspace.
   */
  private readonly contactLinks = signal<ReadonlyMap<string, ContactChannelQuickLink[]>>(new Map());

  /** "Inactive" is relabeled: former = no longer employed ("nicht mehr beschäftigt"). Default: currently employed. */
  protected readonly activities: { value: WorktimeActivity; label: string }[] = [
    {value: 'All', label: 'Alle'},
    {value: 'Active', label: 'Aktiv'},
    {value: 'Inactive', label: 'Ehemalig'}
  ];

  protected readonly loading = signal(false);
  protected readonly error = signal(false);
  protected readonly overview = signal<WorktimeStatsOverviewDto | null>(null);
  protected readonly employees = signal<EmployeeDto[]>([]);

  protected readonly relationships = signal<FilterOption[]>([]);

  protected readonly activity = signal<WorktimeActivity>('Active');
  protected readonly relationshipIds = signal<string[]>([]);
  protected readonly employeeIds = signal<string[]>([]);
  protected readonly startStr = signal(EmployeesComponent.yearStart());
  protected readonly endStr = signal(EmployeesComponent.yearEnd());

  // sorting (client-side on the merged rows)
  protected readonly sortBy = signal<'name' | 'lastShift' | 'hours'>('name');
  protected readonly sortInverse = signal(false);

  // selection mode + batch relationship assignment
  protected readonly selectionMode = signal(false);
  protected readonly selection = signal<ReadonlySet<string>>(new Set());
  protected readonly selectedIds = computed(() => [...this.selection()]);
  protected relationshipPopupVisible = false;

  // add form
  protected readonly addFirst = signal('');
  protected readonly addLast = signal('');
  protected readonly addEmail = signal('');
  protected readonly addStart = signal(EmployeesComponent.today());
  /** Optional employment relationship to assign to the new employee immediately after creation. */
  protected readonly addRelationshipId = signal<string>('');

  /**
   * The creator's explicit answer to "how should this invitation travel", or `null` while they have not given
   * one (#279, #568).
   *
   * Replaces the old "Passwort vergeben / Email senden" checkbox, which mailed a password-reset link. There is
   * no password to set any more: the employee signs in with their own Microsoft/Google/Apple account, and this
   * chooses how the one-time code that binds it reaches them.
   *
   * **Stored as an override, because the default is derived** (#568): with an address in the form the default
   * is „Per E-Mail senden", without one it is „Nur Link erzeugen". Writing that back into a plain signal from
   * an effect would fight the person who just chose otherwise — the next keystroke in the address field would
   * re-arm a mail they had declined. The override survives edits to the address (correcting a typo must not
   * silently change how the invitation travels) and is cleared with the rest of the form.
   */
  private readonly inviteOverride = signal<InviteDelivery | null>(null);

  /**
   * Whether „Per E-Mail senden" can be offered at all: there has to be an address the server would mail to.
   *
   * `looksLikeEmail` rather than a non-empty check (#568), so the three screens that issue an invitation agree
   * on what an address is — and so `neue.person@` does not arm a delivery the server refuses.
   */
  protected readonly canMailInvite = computed(() => looksLikeEmail(this.addEmail()));

  /**
   * The choice that will actually be acted on: „Per E-Mail senden" falls back to „Nur Link erzeugen" the moment
   * the address is cleared.
   *
   * A computed rather than an effect writing back to {@link inviteOverride}, because the correction has to hold
   * for BOTH the rendered selection and the submit — a value that can be out of step with what the control is
   * allowed to offer is how "I picked e-mail" silently becomes "sent to nobody". Restoring the address restores
   * the choice, which is what somebody correcting a typo expects.
   */
  protected readonly effectiveInvite = computed<InviteDelivery>(() => {
    const chosen = this.inviteOverride() ?? (this.canMailInvite() ? 'email' : 'link');
    return chosen === 'email' && !this.canMailInvite() ? 'link' : chosen;
  });

  /**
   * Whether the new employee's invitation may be redeemed ONLY with their stored address (#568, #584): only an
   * account whose provider-verified address equals it may accept it.
   *
   * OFF by default and deliberately so — see {@link canBindAddEmail} for why this is the rare choice here.
   * Kept as a plain signal rather than folded into {@link effectiveInvite}, because the two answer different
   * questions (how the code travels vs. who may use it) and combining them would make "per E-Mail senden"
   * silently imply a constraint nobody asked for.
   *
   * One value for both halves of the wire contract since #584: it decides `BindToEmployeeEmail` **and**
   * `EnforceBoundEmail` together, so the request can never carry the bound-but-unenforced combination that
   * refuses nobody.
   */
  protected readonly addBindEmail = signal(false);

  /**
   * Whether the binding switch can be offered at all: there must be an address the server will bind to, and an
   * invitation for it to constrain.
   *
   * Mirrors the server's own precondition (`RoleGrantService.InviteEmployeeLink` derives the binding from the
   * employee row and answers `E_GRANT_INVITE_EMAIL_REQUIRED` when there is none), so the impossible combination
   * cannot be composed on screen. The value the switch holds is ignored when this is false — see
   * {@link bindOnCreate} — so clearing the address can never smuggle a stale `true` into the request.
   */
  protected readonly canBindAddEmail = computed(() =>
    this.effectiveInvite() !== 'none' && this.canMailInvite());

  /** What the create actually sends for the binding — the switch, gated by whether it may be honoured. */
  protected readonly bindOnCreate = computed(() => this.canBindAddEmail() && this.addBindEmail());

  /** The invitation just issued, shown once in {@link InviteCodeDialogComponent}; null while no dialog is up. */
  protected readonly issuedInvitation = signal<RoleGrantInvitation | null>(null);

  // attribute popup (ported from the retired employee list)
  protected popupEmployee: EmployeeDto | undefined;
  protected attributePopupVisible = false;
  protected mouseEvent?: MouseEvent;

  // download
  protected downloadInformation: DownloadInformation = new DownloadInformation();
  protected downloadExtended = false;

  // end-employment / rehire dialog
  protected readonly lifecycleDialog = signal<LifecycleDialog | null>(null);

  /**
   * The merged, filtered, sorted roster: employees (former included — the segmented filter decides visibility)
   * joined with their worktime metrics by id, narrowed by the employee multi-select and the
   * employment-relationship filter.
   */
  protected readonly rows = computed<EmployeeRow[]>(() => {
    const statsById = new Map((this.overview()?.employees ?? []).map(s => [s.employeeId, s]));
    const employeeSet = new Set(this.employeeIds());
    const relationshipSet = new Set(this.relationshipIds());

    const filtered = this.employees()
      .filter(e => this.matchesActivity(e))
      .filter(e => employeeSet.size === 0 || employeeSet.has(e.id))
      .filter(e => relationshipSet.size === 0 ||
        (e.lastEmploymentRelationship != null && relationshipSet.has(e.lastEmploymentRelationship.id)))
      .map(e => ({employee: e, stats: statsById.get(e.id)}));

    return this.sort(filtered);
  });

  /**
   * The aggregate cards above the table, derived from the VISIBLE (filtered) rows so the summary always matches
   * exactly what the table shows — not the whole company. Hours/shifts come from each row's worktime metrics;
   * the per-month average divides by the number of months the selected date range spans.
   *
   * Rounding (#272): every hour figure is quantized to two decimals, midpoint away from zero, via `roundHours` —
   * the TS mirror of `WorktimeStatsCalculator.RoundHours`.
   *
   * NOTE — this deliberately CANNOT follow the backend's "aggregate from raw, round once" rule, and that is not
   * an oversight. The backend sums the unrounded per-employee hours and rounds the sum once; we only ever
   * receive the ALREADY-ROUNDED row values (`WorktimeStatsDto.hoursWorked`), so the raw doubles simply do not
   * exist on this side. We therefore sum what we were sent and round the result. The two can disagree by at
   * most n × 0.005 (n = visible rows) — i.e. a hundredth or two in practice. Shipping the raw values in the DTO
   * purely to close that gap was rejected: it would widen a public wire contract, and duplicate every hour
   * figure, to reconcile a difference smaller than the display resolution the tiles are read at.
   */
  protected readonly summary = computed(() => {
    const rows = this.rows();
    const stats = rows.map(r => r.stats).filter((s): s is WorktimeStatsDto => s != null);
    const totalHours = stats.reduce((sum, s) => sum + s.hoursWorked, 0);
    const totalShiftCount = stats.reduce((sum, s) => sum + s.shiftCount, 0);
    const employeeCount = rows.length;
    const months = this.monthsInRange();
    return {
      totalHours: roundHours(totalHours),
      totalShiftCount,
      employeeCount,
      // The averages divide the RAW sum and round only the quotient, so the per-employee/per-month figures
      // don't inherit the total's rounding error on top of their own.
      averageHoursPerEmployee: employeeCount === 0 ? 0 : roundHours(totalHours / employeeCount),
      averageHoursPerMonth: months <= 0 ? 0 : roundHours(totalHours / months)
    };
  });

  /**
   * Options for the employee multi-select: only employees matching the active status filter
   * (Alle / Aktiv / Ehemalig), so the picker never offers people the list can't show, by display name.
   */
  protected readonly employeeOptions = computed<FilterOption[]>(() =>
    this.employees()
      .filter(e => this.matchesActivity(e))
      .map(e => ({id: e.id, name: this.fullName(e)}))
      .sort((a, b) => a.name.localeCompare(b.name)));

  protected readonly allSelected = computed(() =>
    this.rows().length > 0 && this.rows().every(r => this.selection().has(r.employee.id)));

  constructor() {
    // The pinned header clone, the pinned horizontal scrollbar clone and the frozen column's scroll-driven
    // edge are all driven by the SHARED PinnedTableController (_ui/tables/pinned-table.ts), so this screen
    // and the Pflicht-Kontakte screen (#558) run one implementation of a construction whose every line is a
    // measured workaround. Everything that used to live here — the passive out-of-zone listeners, the
    // two-way scroll sync and its animation-frame feedback guard, the column measurement, and both hand-over
    // lines — moved there verbatim; the markup and the viewChilds stay here, because a clone's header cells
    // are this screen's header cells (sort buttons and all).
    //
    // The controller's parts accessor reads those viewChild signals, so this effect still re-binds to
    // whichever nodes are currently rendered as the loading / error / empty chain swaps the table in and out,
    // and its onCleanup — which Angular runs both on re-run and on the component's DestroyRef — unbinds them.
    effect(onCleanup => this.pinnedTable.bind(onCleanup));

    // The „Kontakt" chips (#558), loaded for the rows that are actually on screen.
    //
    // An effect over `rows()` rather than a one-shot fetch after `reload()`, because the visible set is a
    // CLIENT-side derivation (status segment, two multi-selects, sort) that the roster load knows nothing
    // about — a roster of 400 with "Aktiv" selected would otherwise pay for 400 ids to render 90. Re-running
    // is cheap by construction: `ContactQuickLinkHttpService` caches per id and shares in-flight requests, so
    // a filter change asks only for ids nobody has asked about yet, in one batched call, and a filter change
    // that widens back onto known ids issues no request at all.
    //
    // Reads `rows()` and writes `contactLinks`, which `rows()` does NOT read — the two cannot form a loop.
    effect(() => {
      const ids = this.rows().map(r => r.employee.id);
      if (!this.canUseContactQuickLinks || ids.length === 0) return;
      // `untracked` is load-bearing, not defensive: when every id is already cached the service answers
      // SYNCHRONOUSLY, so the merge below would read `contactLinks` inside this effect's tracking context and
      // then write it — an effect that depends on its own output, i.e. an infinite loop, and one that would
      // only appear on the second filter change.
      untracked(() => this.loadContactQuickLinks(ids));
    });

    this.destroyRef.onDestroy(() => {
      for (const timer of this.animationTimers.values()) clearTimeout(timer);
      this.animationTimers.clear();
      this.animatingIds.clear();
    });
  }


  // ----- row enter / leave animation (data mutations only, #389) -----

  /**
   * The `animate.enter` class for one row: {@link ROW_ENTER_CLASS} while that employee is marked as animating,
   * otherwise the EMPTY STRING — which Angular treats as "no animation at all", so the row simply appears. That
   * conditional binding is the whole point: switching the status segment, picking people in the employee or
   * relationship multi-select, moving the date range or re-sorting a column inserts and removes rows WITHOUT
   * animation, and only a genuine mutation expands or collapses one.
   */
  protected rowEnterAnimation(id: string): string {
    return this.animatingIds.has(id) ? ROW_ENTER_CLASS : '';
  }

  /**
   * The `animate.leave` counterpart.
   *
   * These two are called LAZILY — Angular compiles an `animate.*` binding into a resolver it invokes at the
   * moment the node is inserted or removed, not during the update pass. That is what makes the leave side work
   * at all: a row that `@for` drops never gets another update pass, so an eagerly captured value would still be
   * the empty string from before the mutation and nothing would ever animate away. (Verified: during a removal
   * pass only the LEAVING row's expression is evaluated.)
   */
  protected rowLeaveAnimation(id: string): string {
    return this.animatingIds.has(id) ? ROW_LEAVE_CLASS : '';
  }

  /** Read live (the OS setting can change mid-session) and guarded, so a non-browser host cannot break it. */
  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Mark one employee's row as "this is a data mutation" for the duration of the animation, so its insertion or
   * removal collapses/expands instead of snapping. Un-marks itself again after {@link ROW_ANIMATION_RESET_MS} —
   * without that, a row added today would still animate when an unrelated filter change removed it an hour
   * later, which is exactly the behaviour this feature is scoped to avoid.
   */
  private markRowAnimating(id: string): void {
    // Reduced motion is honoured HERE, at the source, not only in the stylesheet: with no mark both bindings
    // resolve to the empty string, so Angular applies no class and inserts/removes the row outright. Suppressing
    // the animation in CSS alone is NOT enough and that is not a theoretical worry — measured in a real browser
    // with the media feature emulated, a leaving row with the class but no animation stayed in the DOM forever,
    // because Angular falls back to the computed style and accepts the row's 150ms transition-colors as the
    // thing to wait for. The @media block below is now belt-and-braces behind this.
    if (this.prefersReducedMotion()) return;

    const running = this.animationTimers.get(id);
    if (running) clearTimeout(running);
    this.animatingIds.add(id);
    // Outside the zone: this timer only flips a plain Set that no template binding reads, so waking change
    // detection half a second after every mutation would be pure overhead.
    this.ngZone.runOutsideAngular(() => this.animationTimers.set(id, setTimeout(() => {
      this.animationTimers.delete(id);
      this.animatingIds.delete(id);
    }, ROW_ANIMATION_RESET_MS)));
  }

  ngOnInit(): void {
    // Deep link from other screens: preselect the employment-relationship filter.
    this.route.queryParams.subscribe(params => {
      if (params['employmentRelationship']) this.relationshipIds.set([params['employmentRelationship']]);
    });

    // Wire the stats pipeline BEFORE the first trigger. switchMap cancels an in-flight request when a newer one
    // starts, so out-of-order responses can't leave stale numbers on screen; catchError keeps the stream alive.
    this.statsTrigger.pipe(
      switchMap(() => this.statsHttp.get({
        startDate: this.startStr() || undefined,
        endDate: this.endStr() || undefined,
        // Respect ALL active filters server-side so the long-running query only computes the visible set and the
        // summary reflects exactly what's filtered.
        activity: this.activity(),
        employmentRelationshipIds: this.relationshipIds(),
        employeeIds: this.employeeIds()
      }).pipe(catchError(() => of(null)))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(o => this.overview.set(o));

    // Every employee mutation anywhere in the app funnels through EmployeeService.Replace — the batch
    // relationship popup's ReplaceAndGet, the per-row actions, and SignalR pushes from OTHER users. Patching the
    // roster from that stream is what lets this page drop its post-mutation refetch entirely (#389).
    //
    // EntityChanged is a BehaviorSubject, so subscribing REPLAYS its current value: on a fresh app that is
    // `undefined`, but after any other screen touched an employee it is a stale entity from that screen, which
    // would silently overwrite (or re-append) a row here. skip(1) drops exactly that replayed value and leaves
    // every genuinely NEW emission intact.
    this.employeeService.EntityChanged.pipe(
      skip(1),
      filter((e): e is EmployeeDto => e != null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(e => this.upsertEmployee(e));

    this.loadFilterOptions();
    this.reload();
  }

  // ----- contact quick links (#558) -----

  /** This employee's consented, openable channels — `[]` until they are loaded, and `[]` if there are none. */
  protected contactLinksFor(employeeId: string): ContactChannelQuickLink[] {
    return this.contactLinks().get(employeeId) ?? [];
  }

  /**
   * The legacy Telegram handle carried on the employee profile — `@username`, else `UID: <id>`, else `null`.
   *
   * This is the content of the old „Telegram / Email" column's first half, which #558 merged into „Kontakt"
   * so the roster carries ONE column for every way to reach somebody instead of gaining one per channel
   * family. It is deliberately the *fallback*, never shown beside a chip: a consented Telegram channel and
   * this field are the same channel, and rendering both would show the same person twice — and disagree the
   * moment the employee withdraws consent, because nothing clears this field when they do.
   *
   * The caller renders it as muted, non-interactive text: unlike a chip, nobody consented to it, nothing has
   * proven it reachable, and the profile field can be hand-typed (`manuallyUpdated`). It is a hint about where
   * this person *might* be, which is why the old link — and the `(M)` marker that qualified it — are gone
   * rather than carried over; „unbestätigt" is the only qualification that still matters once a verified chip
   * exists one column over.
   *
   * `UID: ` keeps its space (the old column wrote `UID:1234`): out of a Telegram-labelled column the bare
   * prefix reads as part of the number.
   */
  protected legacyTelegramHandle(employee: EmployeeDto): string | null {
    const username = employee.telegram?.username?.trim();
    if (username) return '@' + username;
    const userId = employee.telegram?.userId?.trim();
    return userId ? 'UID: ' + userId : null;
  }

  /**
   * Fetches the quick links of `ids` and merges them into {@link contactLinks}.
   *
   * Merge, not replace: the map accumulates across filter changes (see the field's own note), and the service
   * returns only the ids of THIS call. A failure is swallowed — a missing chip column costs the manager a
   * click through to the detail page, whereas a banner would put a second, louder failure on a page that
   * already reports its own roster/stats errors, for the least important thing on it.
   */
  private loadContactQuickLinks(ids: string[]): void {
    this.contactQuickLinkHttp.forEmployees(ids).pipe(
      catchError(() => of(new Map<string, ContactChannelQuickLink[]>() as ReadonlyMap<string, ContactChannelQuickLink[]>)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(loaded => {
      if (loaded.size === 0) return;
      const merged = new Map(this.contactLinks());
      for (const [id, channels] of loaded) merged.set(id, channels);
      this.contactLinks.set(merged);
    });
  }

  // ----- filters -----

  // Each roster filter also refreshes the stats so the server recomputes (and the summary reflects) only the
  // filtered set — see the switchMap pipeline in ngOnInit. The roster itself is not refetched (date-independent).

  protected setActivity(value: WorktimeActivity): void {
    this.activity.set(value);
    this.statsTrigger.next();
  }

  protected setRelationships(value: string[] | string | undefined): void {
    this.relationshipIds.set(this.asArray(value));
    this.statsTrigger.next();
  }

  protected setEmployees(value: string[] | string | undefined): void {
    this.employeeIds.set(this.asArray(value));
    this.statsTrigger.next();
  }

  /** Replace the employment-relationship filter with the clicked relationship (table → filter shortcut). */
  protected selectRelationship(id: string): void {
    this.relationshipIds.set([id]);
    this.statsTrigger.next();
  }

  protected setAddRelationship(value: string[] | string | undefined): void {
    this.addRelationshipId.set(this.asArray(value)[0] ?? '');
  }

  /** The creator's explicit answer, which from now on beats the address-derived default (#568). */
  protected setAddInvite(value: InviteDelivery): void {
    this.inviteOverride.set(value);
  }

  protected setStart(value: string): void {
    this.startStr.set(EmployeesComponent.normalizeDate(value));
    // The date range only affects the stats — refresh those, NOT the roster (which is date-independent). This
    // avoids the whole list flickering through its loading state on every date-picker keystroke.
    this.statsTrigger.next();
  }

  protected setEnd(value: string): void {
    this.endStr.set(EmployeesComponent.normalizeDate(value));
    this.statsTrigger.next();
  }

  // ----- data -----

  /**
   * Full reload: the roster and the stats. Used ONLY for the genuine first load (ngOnInit) and the error-state
   * retry — never after a mutation. `loadRoster` raises `loading`, and the template's `@if (loading()) … @else`
   * chain unmounts the whole `overflow-x-auto` wrapper, taking its `scrollLeft` (and the frozen column's scroll
   * position) with it. Mutations therefore patch the affected row via {@link upsertEmployee} instead (#389).
   */
  protected reload(): void {
    this.loadRoster();
    this.statsTrigger.next();
  }

  /**
   * Patch ONE employee into the roster signal without refetching: replace the entry with the same id, or append
   * it when the roster doesn't know it yet (a freshly created employee, or a SignalR push for someone who was
   * hired elsewhere).
   *
   * Signal-correct and surgical: a changed row yields a NEW array with a NEW object at that index while every
   * OTHER element keeps its object identity, so `@for … track r.employee.id` re-renders exactly the one row and
   * leaves the rest of the DOM — and the wrapper's scroll position — untouched.
   *
   * Idempotent by design: applying the same DTO twice is a no-op the second time (it returns the very same array
   * reference, so not even a change-detection pass is triggered). That matters because most mutations land here
   * TWICE — once from the call site's own response and once more when `EmployeeService.Replace` echoes the same
   * entity back through `EntityChanged`.
   */
  private upsertEmployee(employee: EmployeeDto): void {
    this.employees.update(list => {
      const index = list.findIndex(e => e.id === employee.id);
      if (index < 0) return [...list, employee];
      if (list[index] === employee) return list; // already applied — keep the array identity, skip the re-render
      const next = [...list];
      next[index] = employee;
      return next;
    });
  }

  /**
   * Load the employee roster former-inclusive (all=true); the segmented status filter narrows it client-side.
   * The roster does NOT depend on the stats date range, so it is fetched on init (and the error retry) only —
   * never on a date-range change, and never after a mutation: those patch the affected row in place (#389).
   */
  private loadRoster(): void {
    this.loading.set(true);
    this.error.set(false);
    this.employeeHttp.list('', true).subscribe({
      next: list => {
        this.employees.set(list);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(true);
        this.employees.set([]);
        this.loading.set(false);
      }
    });
  }

  private loadFilterOptions(): void {
    this.relationshipHttp.List().subscribe({
      next: list => this.relationships.set(
        list.map(r => ({id: r.id, name: r.name})).sort((a, b) => a.name.localeCompare(b.name)))
    });
  }

  // ----- add -----

  protected add(): void {
    const first = this.addFirst().trim();
    const last = this.addLast().trim();
    if (first === '' || last === '') {
      this.showAlert('Fehler', 'Vor- und Nachname müssen ausgefüllt sein.', AlertLevel.error);
      return;
    }
    const start = this.addStart() ? new Date(this.addStart()) : undefined;
    const relationshipId = this.addRelationshipId();
    const invite = this.effectiveInvite();
    const bind = this.bindOnCreate();
    this.employeeHttp.add(first, last, start, this.addEmail() || undefined).subscribe({
      next: created => {
        // Append the created employee to the roster instead of refetching it — the response IS the new row.
        // Marked FIRST: the mark has to be in place before the signal update renders the row, because that is
        // when Angular reads the animate.enter resolver.
        this.markRowAnimating(created.id);
        this.upsertEmployee(created);
        this.employeeService.Replace(created);
        // Optionally assign the chosen employment relationship to the freshly created employee.
        if (relationshipId) this.assignRelationshipOnCreate(created.id, relationshipId, `${first} ${last}`);
        this.showAlert('Erfolgreich hinzugefügt', `${first} ${last} wurde erfolgreich hinzugefügt.`, AlertLevel.success);
        // The invitation needs the employee id, so it can only be issued once the employee exists — two calls,
        // in this order, always. See inviteOnCreate for what the administrator sees if the second one fails.
        if (invite !== 'none') {
          // Read BEFORE resetAddForm() clears the form — the invitation is issued asynchronously, and a flag
          // read from a signal inside the callback would be the reset value, not the issuer's choice.
          this.inviteOnCreate(created.id, `${first} ${last}`, invite === 'email', bind);
        }
        this.resetAddForm();
        // The aggregate cards are server-computed, so they DO need a refresh — but that only swaps the
        // overview() signal and never touches loading(), so the table keeps its DOM node and its scroll offset.
        this.statsTrigger.next();
      },
      error: () => this.showAlert('Fehler', 'Beim Hinzufügen ist ein Fehler aufgetreten.', AlertLevel.error)
    });
  }

  /**
   * Issue the employee-link invitation for a just-created employee (#279).
   *
   * ### Why this is a second call, and what happens when it fails
   *
   * An invitation names the employee it binds, so it cannot exist before the employee does — creation and
   * invitation are necessarily two requests. That makes a partial outcome possible: the employee is created and
   * the invitation is not. It is reported as exactly that, naming the person and where to finish the job, rather
   * than shown as a failed create (the employee IS there and re-submitting the form would only be refused for a
   * duplicate name) or swallowed (which leaves somebody who cannot sign in and nobody who knows).
   *
   * ### The three delivery outcomes
   *
   * The server always returns the code, whatever became of the mail, so this never has to choose between telling
   * the truth and losing the invitation:
   * - `Sent` — say who it went to; the code is deliberately NOT shown, because a code on screen invites handing
   *   it over a second way and burning the single use.
   * - `NoAddress` / `Failed` — open the dialog after all, and say why. Silently claiming a mail is on its way is
   *   the one outcome that leaves the employee waiting for a message that never arrives.
   */
  private inviteOnCreate(employeeId: string, name: string, byEmail: boolean, bind: boolean): void {
    // Both flags carry the ONE switch (#584): an employee-link invitation is either redeemable only with the
    // stored address or by anybody holding the code, and "bound but not enforced" is the state that refuses
    // nobody. Delivery stays independent, so an unbound invitation is still mailed to the employee.
    this.grantHttp.inviteEmployeeLink(employeeId,
      {BindToEmployeeEmail: bind, DeliverByEmail: byEmail, EnforceBoundEmail: bind}).subscribe({
      next: invitation => {
        if (byEmail && invitation.emailDelivery === InviteEmailDelivery.Sent) {
          this.showAlert('Einladung gesendet',
            `Die Einladung für ${name} wurde an `
            + `${invitation.deliveryEmail ?? invitation.boundEmail ?? 'die hinterlegte Adresse'} gesendet.`
            // The binding is stated on the success path too, not only in the dialog: on a successful send the
            // dialog never opens, so this banner is the ONLY place the issuer learns whether what they just
            // handed out is narrowed to one mailbox — and that decides whether an Apple relay recipient can
            // accept it at all.
            + (invitation.enforceBoundEmail
              ? ` Nur mit ${invitation.boundEmail} einlösbar. Ein Konto mit einer anderen Adresse wird `
                + 'abgewiesen.'
              : ''),
            AlertLevel.success);
          return;
        }

        if (byEmail) {
          this.showAlert('Einladung nicht versendet',
            `Die Einladung für ${name} konnte nicht per E-Mail versendet werden. Geben Sie den Code direkt weiter.`,
            AlertLevel.warning);
        }
        this.issuedInvitation.set(invitation);
      },
      error: err => this.showAlert('Einladung fehlgeschlagen',
        `${name} wurde angelegt, aber die Einladung konnte nicht erstellt werden `
        + `(${EmployeesComponent.inviteErrorMessage(err)}). Sie können sie auf der Registerkarte „Anmeldung“ `
        + 'der Mitarbeiterdetails erstellen.', AlertLevel.error)
    });
  }

  /**
   * German copy for a refused invitation, mapped from the structured `ApiError.code`.
   *
   * Never `error.description`: that is the server's developer-facing English (frontend style guide, "Surfacing
   * an API error — branch on `code`, never render `description`"), and an unmapped code must degrade to German
   * rather than leak it.
   */
  private static inviteErrorMessage(error: unknown): string {
    const code = (error as { error?: { code?: string } } | null)?.error?.code;
    switch (code) {
      case 'E_GRANT_INVITE_FORBIDDEN_SCOPE':
        return 'für diese Firma nicht berechtigt';
      case 'E_EMPLOYEE_INVITE_UNKNOWN_EMPLOYEE':
        return 'der Mitarbeiter wurde nicht gefunden';
      // #568. Reachable here only if this screen's own precondition and the server's disagree about the stored
      // address — the form gates both new options on a syntactically valid one — so the sentence names the
      // remedy rather than restating the rule.
      case 'E_GRANT_INVITE_EMAIL_REQUIRED':
        return 'für „per E-Mail senden“ bzw. „Nur mit dieser E-Mail-Adresse einlösbar“ fehlt eine gültige '
          + 'E-Mail-Adresse';
      default:
        return 'unerwarteter Fehler';
    }
  }

  /**
   * Assign an employment relationship to a just-created employee. The response's `item2` already carries the
   * updated employees, so the row is patched from it — no roster refetch.
   */
  private assignRelationshipOnCreate(employeeId: string, relationshipId: string, name: string): void {
    this.relationshipHttp.setRelationship(relationshipId, [employeeId]).subscribe({
      next: result => {
        for (const updated of result?.item2 ?? []) {
          this.upsertEmployee(updated);
          this.employeeService.Replace(updated);
        }
        this.statsTrigger.next();
      },
      error: () => this.showAlert('Fehler',
        `Das Arbeitsverhältnis für ${name} konnte nicht gesetzt werden.`, AlertLevel.error)
    });
  }

  protected resetAddForm(): void {
    this.addFirst.set('');
    this.addLast.set('');
    this.addEmail.set('');
    this.addStart.set(EmployeesComponent.today());
    // Back to the DERIVED default (#568) rather than to a literal: the form is empty, so there is no address,
    // so the next create starts on „Nur Link erzeugen" — and starts on „Per E-Mail senden" the moment one is
    // typed, which is the rule everywhere an invitation is issued.
    this.inviteOverride.set(null);
    this.addRelationshipId.set('');
    // Back to the declared default, not to whatever the last hire got: the binding is a decision about one
    // person's account, and the next employee is a different person.
    this.addBindEmail.set(false);
  }

  /** The new state of a switch, from its own `change` event. */
  protected isChecked(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  // ----- lifecycle (end employment / rehire — replaces delete) -----

  protected openLifecycleDialog(mode: 'end' | 'rehire', employee: EmployeeDto): void {
    this.lifecycleDialog.set({mode, employee, dateStr: EmployeesComponent.today()});
  }

  protected setDialogDate(dateStr: string): void {
    const dlg = this.lifecycleDialog();
    if (dlg) this.lifecycleDialog.set({...dlg, dateStr});
  }

  protected closeLifecycleDialog(): void {
    this.lifecycleDialog.set(null);
  }

  protected confirmLifecycleDialog(): void {
    const dlg = this.lifecycleDialog();
    if (!dlg) return;
    const name = this.fullName(dlg.employee);

    // Send the picked calendar day as-is (yyyy-MM-dd) — the SERVER interprets the day boundary and the
    // "today ⇒ now" rule in the company's configured time zone, so this never depends on the browser's zone.
    const request = dlg.mode === 'end'
      ? this.employeeHttp.endEmployment(dlg.employee.id, dlg.dateStr || undefined)
      : this.employeeHttp.rehire(dlg.employee.id, dlg.dateStr || undefined);

    request.subscribe({
      next: updated => {
        // The endpoint returns the updated employee — patch that ONE row. If it no longer matches the active
        // status filter (ending an employment while "Aktiv" is selected) it simply drops out of rows() — and
        // because this IS a data mutation, that departure collapses instead of snapping (the mark has to be set
        // before the signal update, which is what triggers the removal).
        this.markRowAnimating(updated.id);
        this.upsertEmployee(updated);
        this.employeeService.Replace(updated);
        this.showAlert('Erfolg', dlg.mode === 'end'
          ? `Die Beschäftigung von ${name} wurde beendet.`
          : `${name} wurde wieder eingestellt.`, AlertLevel.success);
        this.closeLifecycleDialog();
        this.statsTrigger.next();
      },
      error: () => this.showAlert('Fehler', dlg.mode === 'end'
        ? `Die Beschäftigung von ${name} konnte nicht beendet werden.`
        : `${name} konnte nicht wieder eingestellt werden.`, AlertLevel.error)
    });
  }

  // ----- per-row actions -----

  /**
   * Toggle whether the employee may log in. Optimistic: the row flips immediately and reverts if the request
   * fails. Signal-correct — it REPLACES the roster entry with a new object rather than mutating the one the
   * template is bound to, so the change actually propagates through the `employees` signal (an in-place mutation
   * leaves the array and the object identical, which a signal cannot observe and `@for` would never re-render).
   */
  protected setCanLogin(employee: EmployeeDto, allow: boolean): void {
    const optimistic = {...employee, canLogin: allow} as EmployeeDto;
    this.upsertEmployee(optimistic);
    this.employeeHttp.setCanLogin(employee.id, allow).subscribe({
      next: () => this.employeeService.Replace(optimistic),
      error: () => {
        // Put the original entry back — it still carries the pre-toggle value, because nothing mutated it.
        this.upsertEmployee(employee);
        this.showAlert('Fehler', 'Login konnte nicht geändert werden.', AlertLevel.error);
      }
    });
  }

  protected redirectToSettings(id: string): void {
    this.router.navigate([AppRoutes.CompanyEmployeeSettings], {queryParams: {id: id}});
  }

  protected openAttributePopup(e: MouseEvent, employee: EmployeeDto): void {
    this.popupEmployee = employee;
    this.mouseEvent = e;
    this.attributePopupVisible = true;
  }

  /**
   * The batch employment-relationship popup closed. Deliberately does NOT refetch the roster: the popup already
   * pushed every updated employee through `EmployeeService.ReplaceAndGet`, which emits on `EntityChanged` — the
   * subscription in `ngOnInit` patches those rows in place. Only the server-computed aggregate cards are
   * refreshed, which never touches `loading()` and so never unmounts the table (#389).
   */
  protected onRelationshipPopupVisibleChange(visible: boolean): void {
    this.relationshipPopupVisible = visible;
    if (!visible) this.statsTrigger.next();
  }

  /** The relationship shown for a row: the employee's current linker, falling back to the stats resolution. */
  protected relationshipOf(row: EmployeeRow): { id: string; name: string } | null {
    if (row.employee.lastEmploymentRelationship) return row.employee.lastEmploymentRelationship;
    return row.stats?.employmentRelationship ?? null;
  }

  // ----- selection -----

  protected toggleSelectionMode(): void {
    this.selectionMode.set(!this.selectionMode());
    this.selection.set(new Set());
  }

  protected isSelected(id: string): boolean {
    return this.selection().has(id);
  }

  protected setSelected(id: string, selected: boolean): void {
    const next = new Set(this.selection());
    if (selected) next.add(id);
    else next.delete(id);
    this.selection.set(next);
  }

  protected selectAll(state: boolean): void {
    this.selection.set(state ? new Set(this.rows().map(r => r.employee.id)) : new Set());
  }

  // ----- sorting -----

  protected setSort(by: 'name' | 'lastShift' | 'hours'): void {
    if (this.sortBy() === by) this.sortInverse.set(!this.sortInverse());
    else {
      this.sortBy.set(by);
      this.sortInverse.set(false);
    }
  }

  protected sortMarker(by: 'name' | 'lastShift' | 'hours'): string {
    if (this.sortBy() !== by) return '';
    return this.sortInverse() ? '↓' : '↑';
  }

  private sort(rows: EmployeeRow[]): EmployeeRow[] {
    const factor = this.sortInverse() ? -1 : 1;
    const byName = (a: EmployeeRow, b: EmployeeRow) =>
      this.fullName(a.employee).toLowerCase().localeCompare(this.fullName(b.employee).toLowerCase());
    return [...rows].sort((a, b) => {
      switch (this.sortBy()) {
        case 'lastShift': {
          const aDate = a.employee.lastShift ? new Date(a.employee.lastShift).getTime() : Number.MAX_SAFE_INTEGER;
          const bDate = b.employee.lastShift ? new Date(b.employee.lastShift).getTime() : Number.MAX_SAFE_INTEGER;
          return (aDate - bDate) * factor || byName(a, b);
        }
        case 'hours':
          return ((a.stats?.hoursWorked ?? 0) - (b.stats?.hoursWorked ?? 0)) * factor || byName(a, b);
        default:
          return byName(a, b) * factor;
      }
    });
  }

  // ----- helpers -----

  protected fullName(employee: EmployeeDto): string {
    return `${employee.firstName} ${employee.lastName}`.trim();
  }

  /**
   * Fractional number of calendar months the selected (inclusive) date range spans — the divisor for the
   * "Ø Std./Monat" card. Mirrors the server's `AverageDaysPerMonth` (Gregorian 365.2425 ÷ 12) so the client
   * summary matches the per-employee server figures.
   */
  private monthsInRange(): number {
    // Mirror the server's blank-date default (the current calendar year) so a cleared bound doesn't make the
    // months 0 while the server still computes a full-year total — the two must agree.
    const start = new Date(this.startStr() || EmployeesComponent.yearStart());
    const end = new Date(this.endStr() || EmployeesComponent.yearEnd());
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    const days = (end.getTime() - start.getTime()) / 86_400_000 + 1; // +1: the end day is inclusive
    return days <= 0 ? 0 : days / (365.2425 / 12);
  }

  /**
   * employed = a period covers now; upcoming = none does but one starts later (a new hire, or a rehire dated in
   * the future); former = neither. The rule lives in `employmentStatusOf`, shared with the details page (#864).
   */
  protected statusOf(employee: EmployeeDto): EmploymentStatus {
    return employmentStatusOf(employee);
  }

  protected statusLabel(employee: EmployeeDto): string {
    switch (this.statusOf(employee)) {
      case 'employed':
        return 'Aktiv';
      case 'upcoming': {
        const start = employmentStartOf(employee);
        return start ? `Ab ${start.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'})}` : 'Beginnt bald';
      }
      default:
        return 'Nicht mehr beschäftigt';
    }
  }

  /**
   * "Aktiv" = employed or upcoming, "Ehemalig" = former (#864): a new hire whose first day is later belongs to the
   * active staff. Same three-state rule as the status chip, and the same split the server's worktime-stats
   * `Active`/`Inactive` filter applies, so the rows and their statistics stay in step.
   */
  private matchesActivity(employee: EmployeeDto): boolean {
    switch (this.activity()) {
      case 'Active':
        return this.statusOf(employee) !== 'former';
      case 'Inactive':
        return this.statusOf(employee) === 'former';
      default:
        return true;
    }
  }

  private showAlert(title: string, message: string, level: AlertLevel): void {
    this.alerts.createAlertBannerModel(title, message, level, 2000);
    this.alerts.show();
  }

  private asArray(value: string[] | string | undefined): string[] {
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  }

  /**
   * Clamp an out-of-range calendar day to the month's last valid day: e.g. a `Bis` of `2026-06-31` (June has no
   * 31st) becomes `2026-06-30`. Native date inputs can surface such an impossible day; if it reaches the backend
   * unparsed, the server silently falls back to the whole calendar year (so a July shift leaks into a "June"
   * range) while the client can't compute the months (`Ø Std./Monat` → 0) — the two disagree and the summary is
   * both wrong and internally inconsistent. Normalising here keeps the server query and the client math on ONE
   * real range. A blank / unparseable value is left untouched (treated as "no bound").
   */
  private static normalizeDate(value: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!m) return value;
    const year = +m[1], month = +m[2], day = +m[3];
    if (month < 1 || month > 12 || day < 1) return value;
    const lastDay = new Date(year, month, 0).getDate(); // day 0 of the next month = last day of this month
    return `${m[1]}-${m[2]}-${String(Math.min(day, lastDay)).padStart(2, '0')}`;
  }

  /**
   * Today as a `yyyy-MM-dd` string in the BROWSER's local zone — only a sensible default value for the date
   * pickers. It deliberately does NOT drive lifecycle semantics: the server re-interprets the chosen day (and the
   * "today ⇒ now" rule) in the company's configured time zone.
   */
  private static today(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private static yearStart(): string {
    return `${new Date().getFullYear()}-01-01`;
  }

  private static yearEnd(): string {
    return `${new Date().getFullYear()}-12-31`;
  }
}
